import { Injectable } from '@angular/core';
import {
  Location,
  LocationArea,
  LocationAreaField,
  OfferingExecutionType,
  OfferingFilters,
  OfferingFiltersApiQueryParams,
  OfferingFiltersQueryParams,
  OfferingFiltersRouterParams,
} from './models';
import { StorageService } from '../app-state/storage.service';

@Injectable({
  providedIn: 'root',
})
export class OfferingFilterService {
  constructor(private storageService: StorageService) {}

  filtersAreEmpty(filters: OfferingFilters): boolean {
    return this.countNonEmptyFilters(filters) === 0;
  }

  countNonEmptyFilters(filters: OfferingFilters): number {
    const filterGroupsThatShouldNotBeCounted = ['targetGroup', 'includePast', 'includeWaitListed', 'includeCanceled'];

    return Object.keys(filters).filter((filterKey) => {
      if (filterGroupsThatShouldNotBeCounted.includes(filterKey)) {
        return false;
      }

      const filterValue = filters[filterKey];

      if (filterValue === null || filterValue === undefined || (Array.isArray(filterValue) && filterValue.length === 0)) {
        return false;
      }

      const filtersThatShouldNotCountIfFalse = ['onlyBooked'];
      if (filterKey === 'onlyBooked' && filterValue === false) {
        return false;
      }

      return true;
    }).length;
  }

  queryParamsToOfferingFilters(queryParams: OfferingFiltersQueryParams, availableLocations?: Location[]): OfferingFilters {
    const offeringFilters: OfferingFilters = {};

    if (queryParams.query?.trim().length > 0) {
      offeringFilters.query = queryParams.query.trim();
    }

    if (queryParams.state) {
      offeringFilters.state = queryParams.state;
    }

    if (queryParams.categoryIds) {
      if (typeof queryParams.categoryIds === 'string') {
        offeringFilters.categoryIds = [queryParams.categoryIds];
      } else if (Array.isArray(queryParams.categoryIds)) {
        offeringFilters.categoryIds = [...queryParams.categoryIds];
      } else {
        console.warn('Invalid data type for offering filter query param categoryIds', queryParams.categoryIds);
      }
    }

    if (queryParams.sportIds) {
      if (typeof queryParams.sportIds === 'string') {
        offeringFilters.sportIds = [queryParams.sportIds];
      } else if (Array.isArray(queryParams.sportIds)) {
        offeringFilters.sportIds = [...queryParams.sportIds];
      } else {
        console.warn('Invalid data type for offering filter query param sportsIds', queryParams.sportIds);
      }

      if (offeringFilters.sportIds) {
        const oldToNewSportIdMapping = [
          ['7481da62-1eeb-11ee-ad93-0242a0c02fd1', '5adcc518-a057-11f1-b9ae-e63113e88e45'],
          ['169d61a4-41b0-11ee-8dbd-ce9ac48e4e9d', '5ade6378-a057-11f1-99c9-e63113e88e45'],
          ['6eb4bdea-1ccd-11ee-8531-02420d25dd36', '5adcb0aa-a057-11f1-8872-e63113e88e45'],
          ['f4b72896-1711-11ed-aae9-222560b74e07', '5ac5dcf4-a057-11f1-9a0a-e63113e88e45'],
          ['091c1564-f398-11ec-bc96-4217ca18cd30', '5ac24b5c-a057-11f1-af99-e63113e88e45'],
          ['72ae14b2-db58-11ec-8476-4217ca18cd30', '5aad6a7a-a057-11f1-83b4-e63113e88e45'],
          ['ce084476-dbea-11ec-b6b9-222560b74e07', '5ab3db26-a057-11f1-9110-e63113e88e45'],
          ['6719dd84-1eec-11ed-8ea4-4217ca18cd30', '5ac6095e-a057-11f1-8830-e63113e88e45'],
          ['7aa137a8-db58-11ec-9aaf-222560b74e07', '5ab25c24-a057-11f1-a797-e63113e88e45'],
          ['ce082b58-dbea-11ec-9b4e-222560b74e07', '5ab3bd76-a057-11f1-9484-e63113e88e45'],
          ['7f296fac-db58-11ec-974a-ce9ac48e4e9d', '5ab278a8-a057-11f1-afe8-e63113e88e45'],
          ['ef2f86ae-0697-11ee-8982-ce9ac48e4e9d', '5adacb3c-a057-11f1-993b-e63113e88e45'],
          ['7dcb0dca-cc34-11f0-929f-bc24117fdf42', '5b0bf09a-a057-11f1-a269-e63113e88e45'],
          ['885c85c0-2406-11ef-ab4f-02420120eacc', '5aeef0b2-a057-11f1-bf7a-e63113e88e45'],
          ['5a09bb1a-53ce-11ee-a654-4217ca18cd30', '5ade8fba-a057-11f1-8d2a-e63113e88e45'],
          ['d0d40e9c-117d-11ed-9e5d-4217ca18cd30', '5ac571ba-a057-11f1-9617-e63113e88e45'],
          ['6d80bb76-b094-11ee-94cb-ce9ac48e4e9d', '5aeabec0-a057-11f1-aca6-e63113e88e45'],
          ['865a9f72-c9dd-11f0-8389-5a54e0743c62', '5b0bd9e8-a057-11f1-a366-e63113e88e45'],
          ['5d636284-0ecb-11f0-8518-bc2411730653', '5af9c258-a057-11f1-b2b0-e63113e88e45'],
          ['e70fa800-0cbf-11ed-872d-222560b74e07', '5ac47dc8-a057-11f1-becc-e63113e88e45'],
          ['7f63b86c-1fd4-11ee-bedb-0242a0c02fd1', '5ade223c-a057-11f1-983e-e63113e88e45'],
          ['6c7b2738-a4d6-11f0-abcb-0242445ac7d3', '5b00c4ae-a057-11f1-b679-e63113e88e45'],
          ['885c7616-2406-11ef-9cbc-02420120eacc', '5aeed690-a057-11f1-bff4-e63113e88e45'],
          ['efab16a2-3481-11ef-9b6c-024284af7bf4', '5af092be-a057-11f1-9c32-e63113e88e45'],
          ['e7107e60-0cbf-11ed-9bc7-222560b74e07', '5ac4c936-a057-11f1-a6dd-e63113e88e45'],
          ['6b9c6edc-6137-11ef-9c78-024263e15226', '5af18782-a057-11f1-bfd4-e63113e88e45'],
          ['8b3e4332-0601-11ef-83a6-5e272ea961fb', '5aeea2c4-a057-11f1-90d3-e63113e88e45'],
          ['318ab06c-0dad-11ed-aff5-4217ca18cd30', '5ac50324-a057-11f1-938b-e63113e88e45'],
          ['75a41a12-fa82-11ef-844b-ce9ac48e4e9d', '5af91a7e-a057-11f1-bbf6-e63113e88e45'],
          ['b5e8c064-56b5-11ed-962c-4217ca18cd30', '5ad2c392-a057-11f1-ad6b-e63113e88e45'],
          ['9258ebb6-b536-11ee-8bbf-ce9ac48e4e9d', '5aead57c-a057-11f1-a005-e63113e88e45'],
          ['e70fb61a-0cbf-11ed-806a-222560b74e07', '5ac49d4e-a057-11f1-bb35-e63113e88e45'],
          ['e3f5efbe-7e22-11ee-ae75-4217ca18cd30', '5ae5e51c-a057-11f1-aba5-e63113e88e45'],
          ['eae3ff96-2856-11ed-83b6-ce9ac48e4e9d', '5ac77cbc-a057-11f1-91d6-e63113e88e45'],
          ['c80827de-1a34-11ed-bf94-4217ca18cd30', '5ac5f220-a057-11f1-ab88-e63113e88e45'],
          ['7b070dc2-1b48-11ee-883f-4217ca18cd30', '5adc99da-a057-11f1-9c3e-e63113e88e45'],
          ['b693f65a-153a-11f0-98ce-02421ee4cd50', '5afa705e-a057-11f1-9bb3-e63113e88e45'],
          ['b353a9a6-117b-11ed-bfa8-4217ca18cd30', '5ac51a8a-a057-11f1-b357-e63113e88e45'],
          ['891da52a-1fd4-11ee-a5f2-ce9ac48e4e9d', '5ade3de4-a057-11f1-a0a9-e63113e88e45'],
          ['c201ce92-117b-11ed-b464-4217ca18cd30', '5ac548d4-a057-11f1-b8d9-e63113e88e45'],
          ['729660fe-6cef-11ee-8ca1-4217ca18cd30', '5ae0ecc4-a057-11f1-adc0-e63113e88e45'],
          ['185dd4da-baa8-11ee-9dd9-0242962a7fec', '5aeaee7c-a057-11f1-8a7a-e63113e88e45'],
          ['f717a4cc-bc23-11ee-b213-ce9ac48e4e9d', '5aeb045c-a057-11f1-93aa-e63113e88e45'],
          ['e70fe842-0cbf-11ed-acef-222560b74e07', '5ac4b2f2-a057-11f1-9af5-e63113e88e45'],
          ['b50897c0-117b-11ed-83d1-222560b74e07', '5ac52f66-a057-11f1-876b-e63113e88e45'],
          ['e710c172-0cbf-11ed-a9fa-222560b74e07', '5ac4e286-a057-11f1-b344-e63113e88e45'],
          ['47aeeac4-c95b-11ed-a7fa-ce9ac48e4e9d', '5ad92700-a057-11f1-b4d7-e63113e88e45'],
          ['2e6de3ca-42ee-11f1-9318-ea901f4a7835', '5b13a0b0-a057-11f1-b08a-e63113e88e45'],
          ['2c2a5686-24fc-11f0-834f-bc2411422144', '5afaa33a-a057-11f1-a837-e63113e88e45'],
          ['959f31e2-f7d8-11ee-9344-0242d80be38c', '5aee82bc-a057-11f1-8233-e63113e88e45'],
          ['075a40de-db6a-11ec-a52f-222560b74e07', '5ab37690-a057-11f1-8475-e63113e88e45'],
          ['075a33b4-db6a-11ec-85dc-222560b74e07', '5ab35d04-a057-11f1-aeac-e63113e88e45'],
          ['075a4d54-db6a-11ec-a0a1-222560b74e07', '5ab399a4-a057-11f1-83ca-e63113e88e45'],
          ['075a22d4-db6a-11ec-b1b5-222560b74e07', '5ab33da6-a057-11f1-b654-e63113e88e45'],
          ['846efa30-dc2b-11ec-926d-ce9ac48e4e9d', '5ab5320a-a057-11f1-bd11-e63113e88e45'],
          ['1f463ec0-b443-11ed-b865-024254d65211', '5ad7d972-a057-11f1-8d00-e63113e88e45'],
          ['12a3b736-da14-11ee-a4ee-222560b74e07', '5aed4c30-a057-11f1-aa25-e63113e88e45'],
          ['16e0d77c-b443-11ed-bba7-4217ca18cd30', '5ad7bd0c-a057-11f1-b9a5-e63113e88e45'],
          ['a9acbac6-ec8e-11ec-9c05-ce9ac48e4e9d', '5abe3832-a057-11f1-b11c-e63113e88e45'],
          ['a9ac8010-ec8e-11ec-901e-ce9ac48e4e9d', '5abdd95a-a057-11f1-a917-e63113e88e45'],
          ['a9ace7a8-ec8e-11ec-b30e-ce9ac48e4e9d', '5abe7414-a057-11f1-a056-e63113e88e45'],
          ['a9acd89e-ec8e-11ec-b152-ce9ac48e4e9d', '5abe6082-a057-11f1-86e5-e63113e88e45'],
          ['a9acf6c6-ec8e-11ec-80c9-ce9ac48e4e9d', '5abe8a94-a057-11f1-b742-e63113e88e45'],
          ['a9acc96c-ec8e-11ec-914b-ce9ac48e4e9d', '5abe4d22-a057-11f1-90dc-e63113e88e45'],
          ['a9ac915e-ec8e-11ec-a051-ce9ac48e4e9d', '5abdf35e-a057-11f1-9d44-e63113e88e45'],
          ['b5e97eb4-ec8e-11ec-826e-ce9ac48e4e9d', '5abea114-a057-11f1-a28c-e63113e88e45'],
          ['a9acad56-ec8e-11ec-b504-ce9ac48e4e9d', '5abe2428-a057-11f1-ae8a-e63113e88e45'],
          ['a9ac9f46-ec8e-11ec-bbd2-ce9ac48e4e9d', '5abe0f74-a057-11f1-92d8-e63113e88e45'],
          ['cf9f5fcc-4c3f-11f0-82f1-bc2411422144', '5afc33da-a057-11f1-8055-e63113e88e45'],
          ['3832e5d0-c1b9-11ee-bbb1-0242599ac073', '5aeb59fc-a057-11f1-bf82-e63113e88e45'],
          ['1a48705e-a8c1-11ef-9292-f20fa226fd41', '5af5cf04-a057-11f1-9286-e63113e88e45'],
          ['fd29f9de-110e-11ee-9f24-4217ca18cd30', '5adb72ee-a057-11f1-8f25-e63113e88e45'],
          ['d67d8a8c-ec91-11ec-a02e-ce9ac48e4e9d', '5abf7300-a057-11f1-ae6e-e63113e88e45'],
          ['d67d5e04-ec91-11ec-b765-ce9ac48e4e9d', '5abf2602-a057-11f1-bb08-e63113e88e45'],
          ['d67d5094-ec91-11ec-881e-ce9ac48e4e9d', '5abf100e-a057-11f1-88d3-e63113e88e45'],
          ['d67d984c-ec91-11ec-aaff-ce9ac48e4e9d', '5abf8cd2-a057-11f1-8723-e63113e88e45'],
          ['cec92fd4-c199-11ed-847c-02426a51ec4a', '5ad8b202-a057-11f1-8d1a-e63113e88e45'],
          ['d67d7c90-ec91-11ec-b760-ce9ac48e4e9d', '5abf5c4e-a057-11f1-b1d8-e63113e88e45'],
          ['d67d4270-ec91-11ec-9bdf-ce9ac48e4e9d', '5abef61e-a057-11f1-9202-e63113e88e45'],
          ['d67db520-ec91-11ec-bf24-ce9ac48e4e9d', '5abfbb12-a057-11f1-ac44-e63113e88e45'],
          ['d67dc358-ec91-11ec-b724-ce9ac48e4e9d', '5abfd12e-a057-11f1-8b80-e63113e88e45'],
          ['d67da62a-ec91-11ec-afa4-ce9ac48e4e9d', '5abfa56e-a057-11f1-b985-e63113e88e45'],
          ['d67d3064-ec91-11ec-a215-ce9ac48e4e9d', '5abee084-a057-11f1-8232-e63113e88e45'],
          ['d67d6ca0-ec91-11ec-99b7-ce9ac48e4e9d', '5abf42b8-a057-11f1-aa39-e63113e88e45'],
          ['2ce39760-3349-11ed-bf3c-ce9ac48e4e9d', '5ac8eb42-a057-11f1-a7af-e63113e88e45'],
          ['89410f6a-0a94-11ee-bf03-4217ca18cd30', '5adae716-a057-11f1-8088-e63113e88e45'],
          ['e068afcc-3296-11ed-a5f1-ce9ac48e4e9d', '5ac8cd1a-a057-11f1-b6c5-e63113e88e45'],
          ['eb06aaa2-b06b-11ee-9cfb-02428ec3cf6c', '5aea8a7c-a057-11f1-a8cc-e63113e88e45'],
          ['e068a14e-3296-11ed-9d4a-ce9ac48e4e9d', '5ac8af7e-a057-11f1-8ec6-e63113e88e45'],
          ['162e1336-e222-11ee-b9bd-0242032d49a0', '5aed9852-a057-11f1-86bf-e63113e88e45'],
          ['ccc422f4-b06b-11ee-996e-4217ca18cd30', '5aea7244-a057-11f1-b733-e63113e88e45'],
          ['e068893e-3296-11ed-a23b-ce9ac48e4e9d', '5ac890ac-a057-11f1-859b-e63113e88e45'],
          ['f3d0184e-b06b-11ee-af17-ce9ac48e4e9d', '5aeaa5d4-a057-11f1-bb24-e63113e88e45'],
          ['b460a718-6454-11ee-ac94-ce9ac48e4e9d', '5ae0d34c-a057-11f1-9962-e63113e88e45'],
          ['b4609408-6454-11ee-aee8-ce9ac48e4e9d', '5ae09b52-a057-11f1-91d4-e63113e88e45'],
          ['59d833f6-6454-11ee-b648-4217ca18cd30', '5ae05c8c-a057-11f1-9b21-e63113e88e45'],
          ['e1b929d6-e018-11ec-a085-222560b74e07', '5ab618aa-a057-11f1-a876-e63113e88e45'],
          ['e1b9387c-e018-11ec-87e3-222560b74e07', '5ab62e94-a057-11f1-b367-e63113e88e45'],
          ['e1b94a1a-e018-11ec-b211-222560b74e07', '5ab642b2-a057-11f1-9199-e63113e88e45'],
          ['e1b9155e-e018-11ec-831f-222560b74e07', '5ab6019e-a057-11f1-ac6e-e63113e88e45'],
          ['e1b9587a-e018-11ec-a86f-222560b74e07', '5ab657ca-a057-11f1-ab82-e63113e88e45'],
          ['3f5043d2-5d1c-11ee-b3db-ce9ac48e4e9d', '5ae01b96-a057-11f1-adc9-e63113e88e45'],
          ['e1b9693c-e018-11ec-bf3b-222560b74e07', '5ab66b20-a057-11f1-8eea-e63113e88e45'],
          ['e684463e-a5ab-11ef-a964-aa28198788c5', '5af5aa38-a057-11f1-a309-e63113e88e45'],
          ['51918748-e191-11ec-92a3-4217ca18cd30', '5ab6e726-a057-11f1-8311-e63113e88e45'],
          ['e1b97b34-e018-11ec-99f6-222560b74e07', '5ab67ea8-a057-11f1-9ea4-e63113e88e45'],
          ['e1b99a4c-e018-11ec-87aa-222560b74e07', '5ab691ae-a057-11f1-9816-e63113e88e45'],
          ['6f86d810-eca5-11ec-b295-4217ca18cd30', '5ac104cc-a057-11f1-9203-e63113e88e45'],
          ['e1b9ac80-e018-11ec-b272-222560b74e07', '5ab6a68a-a057-11f1-a9d7-e63113e88e45'],
          ['552fcee2-ca5a-11ee-873f-222560b74e07', '5aec5730-a057-11f1-ac3d-e63113e88e45'],
          ['e1b9bff4-e018-11ec-8519-222560b74e07', '5ab6bb16-a057-11f1-889b-e63113e88e45'],
          ['e1b9cf6c-e018-11ec-8515-222560b74e07', '5ab6d1fa-a057-11f1-ae42-e63113e88e45'],
          ['f8f2b7e8-b1cd-11ed-aaa9-ce9ac48e4e9d', '5ad74de0-a057-11f1-a7bf-e63113e88e45'],
          ['100d7ba2-b1ce-11ed-a006-024254d65211', '5ad777f2-a057-11f1-a962-e63113e88e45'],
          ['08487dc2-b1ce-11ed-9eeb-ce9ac48e4e9d', '5ad762ee-a057-11f1-86b3-e63113e88e45'],
          ['1abeb348-b1e9-11ed-afc7-024254d65211', '5ad7a470-a057-11f1-83dc-e63113e88e45'],
          ['1521a21c-b1ce-11ed-8486-024254d65211', '5ad78da0-a057-11f1-8b90-e63113e88e45'],
          ['13929cb0-8d12-11ee-b410-4217ca18cd30', '5ae6f47a-a057-11f1-a501-e63113e88e45'],
          ['178bf008-6b7f-11f0-ab59-86f987394d46', '5aff385a-a057-11f1-b00d-e63113e88e45'],
          ['0e447cf4-ae16-11ee-b516-02428ec3cf6c', '5aea3e28-a057-11f1-9b9d-e63113e88e45'],
          ['bc0dabba-49fb-11ed-9c64-4217ca18cd30', '5acadc5e-a057-11f1-a599-e63113e88e45'],
          ['e0807e98-4a08-11ed-84cc-222560b74e07', '5ad112b8-a057-11f1-9f04-e63113e88e45'],
          ['1790489e-1f1c-11ee-a8f2-0242a0c02fd1', '5adcdb02-a057-11f1-a3ee-e63113e88e45'],
          ['8ece1e8e-bfda-11f0-8c8e-bc24117fdf42', '5b03bdee-a057-11f1-bd3c-e63113e88e45'],
          ['8c6ae9ee-49fc-11ed-8e7e-222560b74e07', '5acbd3e8-a057-11f1-be6f-e63113e88e45'],
          ['bc0db844-49fb-11ed-a79b-4217ca18cd30', '5acaf162-a057-11f1-88a6-e63113e88e45'],
          ['70868abe-4a04-11ed-adb4-ce9ac48e4e9d', '5acd83be-a057-11f1-80c8-e63113e88e45'],
          ['64ff2072-8f89-11ee-83ae-4217ca18cd30', '5ae8354c-a057-11f1-8ff9-e63113e88e45'],
          ['6096c7f8-904f-11ee-9119-02423fff7f05', '5ae84bea-a057-11f1-84e4-e63113e88e45'],
          ['17905b86-1f1c-11ee-9034-0242a0c02fd1', '5adcf20e-a057-11f1-9e20-e63113e88e45'],
          ['64546b9c-4a0b-11ed-80c9-ce9ac48e4e9d', '5ad1f99e-a057-11f1-bbc2-e63113e88e45'],
          ['4aa0ae08-4a07-11ed-9522-4217ca18cd30', '5ad04c70-a057-11f1-9b3a-e63113e88e45'],
          ['a49b0f1a-faaa-11ef-ae79-bc24117fdf42', '5af93180-a057-11f1-b255-e63113e88e45'],
          ['f948a3d8-8d64-11ee-8d9b-02423fff7f05', '5ae70a82-a057-11f1-9bc8-e63113e88e45'],
          ['8c6b01ea-49fc-11ed-8941-222560b74e07', '5acbeb26-a057-11f1-a70b-e63113e88e45'],
          ['0e441016-ae16-11ee-bb28-02428ec3cf6c', '5ae9ded8-a057-11f1-b479-e63113e88e45'],
          ['06113678-f8dc-11ef-8392-ce9ac48e4e9d', '5af904bc-a057-11f1-b058-e63113e88e45'],
          ['7d1a0f9a-408c-11f0-a7de-ce9ac48e4e9d', '5afbc26a-a057-11f1-a888-e63113e88e45'],
          ['8c6b1c84-49fc-11ed-9fc3-222560b74e07', '5acbff1c-a057-11f1-a2ea-e63113e88e45'],
          ['8c6b3f52-49fc-11ed-9b77-222560b74e07', '5acc1362-a057-11f1-82ff-e63113e88e45'],
          ['24dd16ac-0bb6-11f0-9d9e-bc2411730653', '5af98fb8-a057-11f1-9183-e63113e88e45'],
          ['9631a338-6d1b-11f0-9e41-86f987394d46', '5aff7e28-a057-11f1-a329-e63113e88e45'],
          ['17906dc4-1f1c-11ee-8fcc-0242a0c02fd1', '5add064a-a057-11f1-b5d9-e63113e88e45'],
          ['65749e88-ebb2-11f0-8b9a-bc24117fdf42', '5b0da7a0-a057-11f1-b1cd-e63113e88e45'],
          ['8c6b56d6-49fc-11ed-9d13-222560b74e07', '5acc2802-a057-11f1-a810-e63113e88e45'],
          ['caf73846-92aa-11ee-acc8-02423fff7f05', '5ae8c0f2-a057-11f1-9f70-e63113e88e45'],
          ['fb86dace-4a0e-11ed-908d-222560b74e07', '5ad2399a-a057-11f1-a108-e63113e88e45'],
          ['ded03162-b015-11f0-bae7-024248cbd512', '5b019a46-a057-11f1-949c-e63113e88e45'],
          ['b144e90a-ebba-11f0-9221-024214f522cd', '5b0dc2d0-a057-11f1-9691-e63113e88e45'],
          ['bc0dc294-49fb-11ed-8f0f-4217ca18cd30', '5acb0a8a-a057-11f1-81fa-e63113e88e45'],
          ['002abcb6-9050-11ee-849e-4217ca18cd30', '5ae8944c-a057-11f1-a541-e63113e88e45'],
          ['6453d43e-4a0b-11ed-97f9-ce9ac48e4e9d', '5ad1c8a2-a057-11f1-9fb0-e63113e88e45'],
          ['4aa106e6-4a07-11ed-b5d4-4217ca18cd30', '5ad0b73c-a057-11f1-8969-e63113e88e45'],
          ['fb8c5224-4a0e-11ed-9a84-222560b74e07', '5ad28012-a057-11f1-933f-e63113e88e45'],
          ['4aa0f552-4a07-11ed-992d-4217ca18cd30', '5ad09630-a057-11f1-9ada-e63113e88e45'],
          ['2fe4b36a-9b51-11ee-b9e0-ce9ac48e4e9d', '5ae93190-a057-11f1-a4a2-e63113e88e45'],
          ['002ace22-9050-11ee-a50c-4217ca18cd30', '5ae8ab44-a057-11f1-821f-e63113e88e45'],
          ['7089320a-4a04-11ed-9796-ce9ac48e4e9d', '5aceed44-a057-11f1-9511-e63113e88e45'],
          ['bbd5d742-27d7-11ef-8304-024289afb0db', '5aef0aac-a057-11f1-a34f-e63113e88e45'],
          ['17907d28-1f1c-11ee-afee-0242a0c02fd1', '5add1bee-a057-11f1-a332-e63113e88e45'],
          ['7086a67a-4a04-11ed-9237-ce9ac48e4e9d', '5acd9908-a057-11f1-9a66-e63113e88e45'],
          ['8c6b6a2c-49fc-11ed-8dbf-222560b74e07', '5acc3cca-a057-11f1-bbc4-e63113e88e45'],
          ['7087c15e-4a04-11ed-b14e-ce9ac48e4e9d', '5ace221a-a057-11f1-94ab-e63113e88e45'],
          ['70883cd8-4a04-11ed-aa59-ce9ac48e4e9d', '5ace947a-a057-11f1-8c80-e63113e88e45'],
          ['8c6ba37a-49fc-11ed-ae90-222560b74e07', '5acc5250-a057-11f1-a636-e63113e88e45'],
          ['caa882ba-9f3d-11ee-9a74-02428ec3cf6c', '5ae98424-a057-11f1-9579-e63113e88e45'],
          ['7086ba0c-4a04-11ed-9947-ce9ac48e4e9d', '5acdad44-a057-11f1-8e00-e63113e88e45'],
          ['7087d28e-4a04-11ed-b44c-ce9ac48e4e9d', '5ace357a-a057-11f1-acfa-e63113e88e45'],
          ['1790c2ba-1f1c-11ee-89a0-0242a0c02fd1', '5add7120-a057-11f1-ad9f-e63113e88e45'],
          ['0e43dda8-ae16-11ee-8934-02428ec3cf6c', '5ae9ae54-a057-11f1-b641-e63113e88e45'],
          ['70885556-4a04-11ed-8d24-ce9ac48e4e9d', '5acead2a-a057-11f1-862a-e63113e88e45'],
          ['17908c78-1f1c-11ee-9d70-0242a0c02fd1', '5add3110-a057-11f1-8940-e63113e88e45'],
          ['ffe5e1f4-8d11-11ee-a783-ce9ac48e4e9d', '5ae6bf14-a057-11f1-9a29-e63113e88e45'],
          ['07fd6bfa-8d12-11ee-bd09-ce9ac48e4e9d', '5ae6d88c-a057-11f1-92cd-e63113e88e45'],
          ['d0bc2e9c-2228-11ed-acb4-ce9ac48e4e9d', '5ac67c68-a057-11f1-88e6-e63113e88e45'],
          ['fb851d24-4a0e-11ed-9c67-222560b74e07', '5ad20e20-a057-11f1-896c-e63113e88e45'],
          ['cf0a8fb4-9d7c-11ee-8b04-4217ca18cd30', '5ae95aee-a057-11f1-8cfa-e63113e88e45'],
          ['17909dee-1f1c-11ee-a056-0242a0c02fd1', '5add4646-a057-11f1-aa85-e63113e88e45'],
          ['0e445ed6-ae16-11ee-b60f-02428ec3cf6c', '5aea29a6-a057-11f1-874c-e63113e88e45'],
          ['d0a78d32-8dd6-11ee-9f82-4217ca18cd30', '5ae72620-a057-11f1-90d6-e63113e88e45'],
          ['9c85fa30-8fa9-11f0-8e8c-0abc35d2cdec', '5affeebc-a057-11f1-9be5-e63113e88e45'],
          ['d867fca0-8dd6-11ee-93be-ce9ac48e4e9d', '5ae73cbe-a057-11f1-b9b9-e63113e88e45'],
          ['e080f9fe-4a08-11ed-95ea-222560b74e07', '5ad183c4-a057-11f1-a684-e63113e88e45'],
          ['7088fc90-4a04-11ed-bd1b-ce9ac48e4e9d', '5acec35a-a057-11f1-b6de-e63113e88e45'],
          ['fb87b354-4a0e-11ed-ad15-222560b74e07', '5ad25646-a057-11f1-920d-e63113e88e45'],
          ['e0808e9c-4a08-11ed-9348-222560b74e07', '5ad13694-a057-11f1-8fa0-e63113e88e45'],
          ['e080ada0-4a08-11ed-a143-222560b74e07', '5ad1535e-a057-11f1-8df1-e63113e88e45'],
          ['7086db18-4a04-11ed-bc44-ce9ac48e4e9d', '5acdc0ea-a057-11f1-b5c6-e63113e88e45'],
          ['1790af0a-1f1c-11ee-9fe4-0242a0c02fd1', '5add5b90-a057-11f1-894a-e63113e88e45'],
          ['0e44940a-ae16-11ee-b840-02428ec3cf6c', '5aea5c3c-a057-11f1-b0f2-e63113e88e45'],
          ['8c6bb18a-49fc-11ed-8b01-222560b74e07', '5acc66b4-a057-11f1-8c67-e63113e88e45'],
          ['1790d67e-1f1c-11ee-916b-0242a0c02fd1', '5add8e4e-a057-11f1-8305-e63113e88e45'],
          ['8c6bbda6-49fc-11ed-96e5-222560b74e07', '5acc7adc-a057-11f1-a10d-e63113e88e45'],
          ['6453f75c-4a0b-11ed-9f07-ce9ac48e4e9d', '5ad1df40-a057-11f1-915b-e63113e88e45'],
          ['3da71bd2-0888-11f0-9ac0-bc2411730653', '5af96010-a057-11f1-8ebc-e63113e88e45'],
          ['4aa0bdd0-4a07-11ed-bbe0-4217ca18cd30', '5ad069ee-a057-11f1-8145-e63113e88e45'],
          ['e080d208-4a08-11ed-8fab-222560b74e07', '5ad16efc-a057-11f1-a924-e63113e88e45'],
          ['5de558d6-99c7-11ee-a894-ce9ac48e4e9d', '5ae91da4-a057-11f1-a564-e63113e88e45'],
          ['0b5a789c-9463-11f0-a700-bc2411422144', '5b00240e-a057-11f1-a633-e63113e88e45'],
          ['fb8d27d0-4a0e-11ed-8b2b-222560b74e07', '5ad294e4-a057-11f1-89d5-e63113e88e45'],
          ['4aa11000-4a07-11ed-a24d-4217ca18cd30', '5ad0d4d8-a057-11f1-8876-e63113e88e45'],
          ['4eaec216-9c2f-11ee-97b8-02423fff7f05', '5ae945a4-a057-11f1-a96f-e63113e88e45'],
          ['be464dce-98c4-11ee-bb66-ce9ac48e4e9d', '5ae8d646-a057-11f1-81fb-e63113e88e45'],
          ['7087ee22-4a04-11ed-8fb7-ce9ac48e4e9d', '5ace4ccc-a057-11f1-a2a0-e63113e88e45'],
          ['ae562a4c-8ea1-11ee-8e9a-02423fff7f05', '5ae7ada2-a057-11f1-ba5c-e63113e88e45'],
          ['8c6bd016-49fc-11ed-aa73-222560b74e07', '5acc9058-a057-11f1-89b5-e63113e88e45'],
          ['8c6bf3c0-49fc-11ed-98a8-222560b74e07', '5acca354-a057-11f1-91f3-e63113e88e45'],
          ['ec07326c-ee9c-11ef-8cdc-ce9ac48e4e9d', '5af86d68-a057-11f1-80e9-e63113e88e45'],
          ['ebe11dee-c66f-11ee-96e6-0242599ac073', '5aeb6fd2-a057-11f1-ad7a-e63113e88e45'],
          ['1abe2f04-c555-11f0-8a6c-5a54e0743c62', '5b04052e-a057-11f1-896b-e63113e88e45'],
          ['6fd5b5c4-9d8a-11ee-a895-ce9ac48e4e9d', '5ae96eb2-a057-11f1-85f9-e63113e88e45'],
          ['fb85fbc2-4a0e-11ed-af7d-222560b74e07', '5ad22216-a057-11f1-9655-e63113e88e45'],
          ['89dc3c70-011c-11f1-a593-222560b74e07', '5b0f01f4-a057-11f1-b5b4-e63113e88e45'],
          ['48cce55e-98c8-11ee-8f9a-4217ca18cd30', '5ae9094a-a057-11f1-b8b2-e63113e88e45'],
          ['fb8b3df8-4a0e-11ed-afe7-222560b74e07', '5ad26b9a-a057-11f1-8986-e63113e88e45'],
          ['a8bdbf1c-046a-11ee-8a86-02420e37ec7a', '5adab5b6-a057-11f1-aa3f-e63113e88e45'],
          ['b6093d74-8ea1-11ee-b4aa-4217ca18cd30', '5ae7c788-a057-11f1-a92b-e63113e88e45'],
          ['8c6c05f4-49fc-11ed-863f-222560b74e07', '5accbd80-a057-11f1-aee7-e63113e88e45'],
          ['7086ff1c-4a04-11ed-8fc1-ce9ac48e4e9d', '5acdd3c8-a057-11f1-bffd-e63113e88e45'],
          ['dd83fbf8-8dfe-11ee-87e0-ce9ac48e4e9d', '5ae77dfa-a057-11f1-b331-e63113e88e45'],
          ['63239c9e-b023-11f0-a3b1-024248cbd512', '5b01ae00-a057-11f1-8abf-e63113e88e45'],
          ['d4a6cc5e-8dfe-11ee-b707-4217ca18cd30', '5ae75cb2-a057-11f1-82ac-e63113e88e45'],
          ['1790e826-1f1c-11ee-b27c-0242a0c02fd1', '5adda6d6-a057-11f1-bc79-e63113e88e45'],
          ['bf8f32b8-7fb0-11ee-9d90-02425069021c', '5ae6a998-a057-11f1-8184-e63113e88e45'],
          ['bd8f5d1c-8ea1-11ee-b587-ce9ac48e4e9d', '5ae7dd18-a057-11f1-b092-e63113e88e45'],
          ['bc0dd4e6-49fb-11ed-8247-4217ca18cd30', '5acb3870-a057-11f1-b2cf-e63113e88e45'],
          ['bc0dcbc2-49fb-11ed-926e-4217ca18cd30', '5acb2240-a057-11f1-a098-e63113e88e45'],
          ['8c6c1544-49fc-11ed-a35b-222560b74e07', '5accd446-a057-11f1-83d8-e63113e88e45'],
          ['70896748-4a04-11ed-95d8-ce9ac48e4e9d', '5acf154e-a057-11f1-b18e-e63113e88e45'],
          ['7089b46e-4a04-11ed-98e8-ce9ac48e4e9d', '5acfa20c-a057-11f1-be13-e63113e88e45'],
          ['70897de6-4a04-11ed-8e0a-ce9ac48e4e9d', '5acf3bbe-a057-11f1-93ff-e63113e88e45'],
          ['708996d2-4a04-11ed-b52d-ce9ac48e4e9d', '5acf6e0e-a057-11f1-a765-e63113e88e45'],
          ['7089d232-4a04-11ed-ae3d-ce9ac48e4e9d', '5acfdd62-a057-11f1-b82a-e63113e88e45'],
          ['70894c5e-4a04-11ed-bbc7-ce9ac48e4e9d', '5acf0176-a057-11f1-b84c-e63113e88e45'],
          ['708a0540-4a04-11ed-acc3-ce9ac48e4e9d', '5ad02eac-a057-11f1-af90-e63113e88e45'],
          ['7089e52e-4a04-11ed-8be7-ce9ac48e4e9d', '5ad01192-a057-11f1-b097-e63113e88e45'],
          ['e0810764-4a08-11ed-ba7c-222560b74e07', '5ad19972-a057-11f1-97f4-e63113e88e45'],
          ['e081154c-4a08-11ed-80f5-222560b74e07', '5ad1b0ce-a057-11f1-b3b8-e63113e88e45'],
          ['80b38c84-ac09-11ef-a6a5-f20fa226fd41', '5af7340c-a057-11f1-a357-e63113e88e45'],
          ['bc0ddd92-49fb-11ed-9e51-4217ca18cd30', '5acb4e64-a057-11f1-ae72-e63113e88e45'],
          ['c8248de2-8ea1-11ee-91c5-4217ca18cd30', '5ae7f24e-a057-11f1-993a-e63113e88e45'],
          ['8c6c38da-49fc-11ed-9798-222560b74e07', '5acce9f4-a057-11f1-9528-e63113e88e45'],
          ['7087fdea-4a04-11ed-a161-ce9ac48e4e9d', '5ace65f4-a057-11f1-b2c9-e63113e88e45'],
          ['2abe457c-8ed6-11ee-be6e-ce9ac48e4e9d', '5ae81fbc-a057-11f1-9336-e63113e88e45'],
          ['1790f8f2-1f1c-11ee-80df-0242a0c02fd1', '5addba90-a057-11f1-8bcb-e63113e88e45'],
          ['002aa5aa-9050-11ee-8074-4217ca18cd30', '5ae87a84-a057-11f1-9ecb-e63113e88e45'],
          ['e7e86cfc-904f-11ee-8920-4217ca18cd30', '5ae861de-a057-11f1-b223-e63113e88e45'],
          ['70891482-4a04-11ed-a58e-ce9ac48e4e9d', '5aced89a-a057-11f1-b1b5-e63113e88e45'],
          ['86f98bc6-8dff-11ee-9c11-4217ca18cd30', '5ae796a0-a057-11f1-8f03-e63113e88e45'],
          ['708733d8-4a04-11ed-8d48-ce9ac48e4e9d', '5acde7fa-a057-11f1-bcf8-e63113e88e45'],
          ['2b1b2e62-daf9-11ee-85f3-222560b74e07', '5aed66b6-a057-11f1-8b04-e63113e88e45'],
          ['ecc74108-dafa-11ee-aa89-222560b74e07', '5aed7ea8-a057-11f1-b67b-e63113e88e45'],
          ['44d5d1ce-ae15-11ee-b7e2-02428ec3cf6c', '5ae997e8-a057-11f1-af6f-e63113e88e45'],
          ['70881ad2-4a04-11ed-b272-ce9ac48e4e9d', '5ace7af8-a057-11f1-a28c-e63113e88e45'],
          ['b245dde0-ed5f-11f0-a892-bc24117fdf42', '5b0ddb08-a057-11f1-8528-e63113e88e45'],
          ['a7d498f2-cf2e-11ee-a3a7-024220a33380', '5aed339e-a057-11f1-bed4-e63113e88e45'],
          ['8c6c467c-49fc-11ed-998f-222560b74e07', '5accffa2-a057-11f1-a47d-e63113e88e45'],
          ['bc0de65c-49fb-11ed-bdd1-4217ca18cd30', '5acb6a02-a057-11f1-9dcc-e63113e88e45'],
          ['d0e049d0-8ea1-11ee-8e54-02423fff7f05', '5ae80a2c-a057-11f1-957c-e63113e88e45'],
          ['8c6c6ed6-49fc-11ed-8b31-222560b74e07', '5acd15fa-a057-11f1-8b56-e63113e88e45'],
          ['17910a9a-1f1c-11ee-9e57-0242a0c02fd1', '5addce18-a057-11f1-b426-e63113e88e45'],
          ['0e43f86a-ae16-11ee-bd22-02428ec3cf6c', '5ae9c678-a057-11f1-9ebc-e63113e88e45'],
          ['0e442a56-ae16-11ee-95d8-02428ec3cf6c', '5ae9f24c-a057-11f1-890a-e63113e88e45'],
          ['f52aa54c-06d8-11ef-9730-5e272ea961fb', '5aeebc96-a057-11f1-975d-e63113e88e45'],
          ['17911a26-1f1c-11ee-95ec-0242a0c02fd1', '5adde33a-a057-11f1-b42a-e63113e88e45'],
          ['8c6c8894-49fc-11ed-9752-222560b74e07', '5acd2afe-a057-11f1-848d-e63113e88e45'],
          ['e4f8ef40-7f00-11ee-a24d-4217ca18cd30', '5ae693b8-a057-11f1-8b0f-e63113e88e45'],
          ['4aa0d518-4a07-11ed-89d4-4217ca18cd30', '5ad08078-a057-11f1-9dd5-e63113e88e45'],
          ['161ce484-98c7-11ee-8155-ce9ac48e4e9d', '5ae8edb6-a057-11f1-965c-e63113e88e45'],
          ['1791282c-1f1c-11ee-867f-0242a0c02fd1', '5addf7da-a057-11f1-b5a6-e63113e88e45'],
          ['8c6ca19e-49fc-11ed-beef-222560b74e07', '5acd4160-a057-11f1-85d2-e63113e88e45'],
          ['7087a4f8-4a04-11ed-9e3b-ce9ac48e4e9d', '5ace0faa-a057-11f1-8307-e63113e88e45'],
          ['bc0e2022-49fb-11ed-9621-4217ca18cd30', '5acb8186-a057-11f1-9e6e-e63113e88e45'],
          ['1791372c-1f1c-11ee-acaa-0242a0c02fd1', '5ade0d06-a057-11f1-93b4-e63113e88e45'],
          ['4aa135d0-4a07-11ed-bba6-4217ca18cd30', '5ad0f260-a057-11f1-907e-e63113e88e45'],
          ['70876376-4a04-11ed-984e-ce9ac48e4e9d', '5acdfb1e-a057-11f1-930c-e63113e88e45'],
          ['bc0e2a04-49fb-11ed-a723-4217ca18cd30', '5acb9694-a057-11f1-bc6c-e63113e88e45'],
          ['bc0e392c-49fb-11ed-bb42-4217ca18cd30', '5acbaada-a057-11f1-9204-e63113e88e45'],
          ['bc0e4188-49fb-11ed-9228-4217ca18cd30', '5acbbf52-a057-11f1-9137-e63113e88e45'],
          ['8c6cb788-49fc-11ed-87ea-222560b74e07', '5acd5880-a057-11f1-89e1-e63113e88e45'],
          ['0e444504-ae16-11ee-a68a-02428ec3cf6c', '5aea0f48-a057-11f1-8c22-e63113e88e45'],
          ['fb90bc56-4a0e-11ed-abef-222560b74e07', '5ad2a9ca-a057-11f1-9430-e63113e88e45'],
          ['8c6ccb06-49fc-11ed-9826-222560b74e07', '5acd6d16-a057-11f1-bc95-e63113e88e45'],
          ['f2f2da0a-e72d-11ec-bcf6-222560b74e07', '5ab7a864-a057-11f1-8c24-e63113e88e45'],
          ['21a9d34e-e72e-11ec-9909-ce9ac48e4e9d', '5ab7bf84-a057-11f1-9f74-e63113e88e45'],
          ['a2243ef0-e8d3-11ec-a6f5-ce9ac48e4e9d', '5ab81bfa-a057-11f1-a0fc-e63113e88e45'],
          ['a22452dc-e8d3-11ec-8911-ce9ac48e4e9d', '5ab82fd2-a057-11f1-8d27-e63113e88e45'],
          ['c46e429e-ebee-11ec-8025-4217ca18cd30', '5ab9574a-a057-11f1-a35a-e63113e88e45'],
          ['65a9a6b6-dc0f-11ec-a8ee-4217ca18cd30', '5ab4b6c2-a057-11f1-9219-e63113e88e45'],
          ['386982de-dc0f-11ec-9891-222560b74e07', '5ab4679e-a057-11f1-ad7b-e63113e88e45'],
          ['65a99c70-dc0f-11ec-b664-4217ca18cd30', '5ab49eee-a057-11f1-b703-e63113e88e45'],
          ['65a9b066-dc0f-11ec-a3ac-4217ca18cd30', '5ab4cb3a-a057-11f1-93ae-e63113e88e45'],
          ['65a98e06-dc0f-11ec-9ca7-4217ca18cd30', '5ab483dc-a057-11f1-ac65-e63113e88e45'],
          ['278e43a4-4187-11ed-956e-4217ca18cd30', '5aca676a-a057-11f1-8258-e63113e88e45'],
          ['278e3670-4187-11ed-81b4-4217ca18cd30', '5aca4f1e-a057-11f1-aa3a-e63113e88e45'],
          ['16c0acce-4187-11ed-868c-4217ca18cd30', '5aca34b6-a057-11f1-b77c-e63113e88e45'],
          ['145b437e-ec94-11ec-98c2-4217ca18cd30', '5ac07a16-a057-11f1-b140-e63113e88e45'],
          ['145b4c84-ec94-11ec-a56c-4217ca18cd30', '5ac0937a-a057-11f1-a1b1-e63113e88e45'],
          ['145b54b8-ec94-11ec-a920-4217ca18cd30', '5ac0b33c-a057-11f1-b171-e63113e88e45'],
          ['145b3938-ec94-11ec-baa9-4217ca18cd30', '5ac05b80-a057-11f1-9167-e63113e88e45'],
          ['145b2344-ec94-11ec-b7df-4217ca18cd30', '5ac04050-a057-11f1-ad4a-e63113e88e45'],
          ['145b5d6e-ec94-11ec-a31a-4217ca18cd30', '5ac0d2f4-a057-11f1-80df-e63113e88e45'],
          ['145b655c-ec94-11ec-9724-4217ca18cd30', '5ac0ec26-a057-11f1-8057-e63113e88e45'],
          ['3e4b1d9e-ec85-11ec-bc86-222560b74e07', '5aba03b6-a057-11f1-827c-e63113e88e45'],
          ['3e4b89c8-ec85-11ec-b485-222560b74e07', '5ab88702-a057-11f1-ab2b-e63113e88e45'],
          ['46a1b682-560d-11ed-83e5-ce9ac48e4e9d', '5ab8ffd4-a057-11f1-96ef-e63113e88e45'],
          ['3e4b6254-ec85-11ec-af33-222560b74e07', '5aba2c56-a057-11f1-b028-e63113e88e45'],
          ['3e4b7d5c-ec85-11ec-90d4-222560b74e07', '5ab89c4c-a057-11f1-8378-e63113e88e45'],
          ['3e4b7168-ec85-11ec-850a-222560b74e07', '5ab8c5c8-a057-11f1-8a98-e63113e88e45'],
          ['8d70724e-da61-11ef-bdf7-4217ca18cd30', '5abc75a6-a057-11f1-84a0-e63113e88e45'],
          ['d284a01a-8048-11f1-80c8-8a999dcc40f3', '5abcf80a-a057-11f1-b52e-e63113e88e45'],
          ['d79fc7ec-8024-11f1-a88d-d23673154e14', '5b15bd64-a057-11f1-9e98-e63113e88e45'],
          ['705dfef8-804e-11f1-b11f-8a999dcc40f3', '5aba8822-a057-11f1-bd8b-e63113e88e45'],
          ['3e4b35a4-ec85-11ec-88ab-222560b74e07', '5aba1928-a057-11f1-92e0-e63113e88e45'],
          ['d807a13c-ec85-11ec-8bb9-4217ca18cd30', '5ab88702-a057-11f1-ab2b-e63113e88e45'],
          ['fd107120-5b3a-11f1-9364-fabab3755241', '5ab8ffd4-a057-11f1-96ef-e63113e88e45'],
          ['d807ac22-ec85-11ec-97d0-4217ca18cd30', '5ab8c5c8-a057-11f1-8a98-e63113e88e45'],
          ['dc23ec54-e69c-11ee-aef3-0242032d49a0', '5ab8b074-a057-11f1-99ce-e63113e88e45'],
          ['fd0fc874-5b3a-11f1-8745-fabab3755241', '5b13e0de-a057-11f1-875c-e63113e88e45'],
          ['c906b45c-5b3a-11f1-836f-fabab3755241', '5aba8822-a057-11f1-bd8b-e63113e88e45'],
          ['fd108516-5b3a-11f1-91ff-fabab3755241', '5ad52844-a057-11f1-9cb8-e63113e88e45'],
          ['fd10a794-5b3a-11f1-864c-fabab3755241', '5ad513ae-a057-11f1-a1b7-e63113e88e45'],
          ['fd1096a0-5b3a-11f1-a333-fabab3755241', '5b13fd12-a057-11f1-b94f-e63113e88e45'],
          ['af012284-0cb7-11ed-84ea-222560b74e07', '5ac36c26-a057-11f1-b1a6-e63113e88e45'],
          ['af0223c8-0cb7-11ed-bb93-222560b74e07', '5ac3c572-a057-11f1-b1a2-e63113e88e45'],
          ['fc0d4300-ebd3-11ec-9f63-ce9ac48e4e9d', '5ab88702-a057-11f1-ab2b-e63113e88e45'],
          ['af02323c-0cb7-11ed-8818-222560b74e07', '5ac3da6c-a057-11f1-8c7d-e63113e88e45'],
          ['af024f42-0cb7-11ed-b64c-222560b74e07', '5ac40546-a057-11f1-9b35-e63113e88e45'],
          ['fc0d9bc0-ebd3-11ec-bb88-ce9ac48e4e9d', '5ab8ffd4-a057-11f1-96ef-e63113e88e45'],
          ['fc0d56b0-ebd3-11ec-8c38-ce9ac48e4e9d', '5ab89c4c-a057-11f1-8378-e63113e88e45'],
          ['af019980-0cb7-11ed-8e44-222560b74e07', '5ac38300-a057-11f1-b73e-e63113e88e45'],
          ['fc0d73a2-ebd3-11ec-85e1-ce9ac48e4e9d', '5ab8c5c8-a057-11f1-8a98-e63113e88e45'],
          ['fc0d6600-ebd3-11ec-8d9c-ce9ac48e4e9d', '5ab8b074-a057-11f1-99ce-e63113e88e45'],
          ['b3e3c61c-f60a-11ec-b96d-222560b74e07', '5abc75a6-a057-11f1-84a0-e63113e88e45'],
          ['401e2772-ee17-11ec-b93a-222560b74e07', '5ac141b2-a057-11f1-95ef-e63113e88e45'],
          ['af0106fa-0cb7-11ed-b364-222560b74e07', '5ac35678-a057-11f1-a26c-e63113e88e45'],
          ['fc0d81b2-ebd3-11ec-bc5e-ce9ac48e4e9d', '5ab8da18-a057-11f1-b576-e63113e88e45'],
          ['af02044c-0cb7-11ed-aef7-222560b74e07', '5ac39bce-a057-11f1-b7a4-e63113e88e45'],
          ['af02410a-0cb7-11ed-aa73-222560b74e07', '5ac3f042-a057-11f1-aacb-e63113e88e45'],
          ['af02157c-0cb7-11ed-a25b-222560b74e07', '5ac3b050-a057-11f1-ab1b-e63113e88e45'],
          ['401e4ea0-ee17-11ec-8132-222560b74e07', '5ac15d46-a057-11f1-8188-e63113e88e45'],
          ['380408d0-1864-11ef-87ff-02420120eacc', '5aba1928-a057-11f1-92e0-e63113e88e45'],
          ['fc0d8e6e-ebd3-11ec-a555-ce9ac48e4e9d', '5ab8ed00-a057-11f1-91b9-e63113e88e45'],
          ['3cdbd9b4-edc5-11ef-9c97-ce9ac48e4e9d', '5af8562a-a057-11f1-b430-e63113e88e45'],
          ['5f9c65fe-586f-11ef-b7d6-427ed5aa0725', '5ab8ffd4-a057-11f1-96ef-e63113e88e45'],
          ['3cdbe670-edc5-11ef-90b8-ce9ac48e4e9d', '5aba2c56-a057-11f1-b028-e63113e88e45'],
          ['94978776-ed3d-11ef-b054-bc24117fdf42', '5af7f950-a057-11f1-b90a-e63113e88e45'],
          ['0745fd1a-c3fc-11ed-aa2e-02426a51ec4a', '5ab8c5c8-a057-11f1-8a98-e63113e88e45'],
          ['94979806-ed3d-11ef-a64d-bc24117fdf42', '5ad94460-a057-11f1-adcb-e63113e88e45'],
          ['9497a968-ed3d-11ef-8833-bc24117fdf42', '5af80f94-a057-11f1-a0b0-e63113e88e45'],
          ['9496ef14-ed3d-11ef-bb8c-bc24117fdf42', '5aba8822-a057-11f1-bd8b-e63113e88e45'],
          ['3cdbbeb6-edc5-11ef-98c4-ce9ac48e4e9d', '5af82484-a057-11f1-9f51-e63113e88e45'],
          ['3cdb40f8-edc5-11ef-9bfe-ce9ac48e4e9d', '5ad513ae-a057-11f1-a1b7-e63113e88e45'],
          ['94976f2a-ed3d-11ef-a88a-bc24117fdf42', '5ad5529c-a057-11f1-87f4-e63113e88e45'],
          ['3cdbcd0c-edc5-11ef-86b6-ce9ac48e4e9d', '5af83f82-a057-11f1-88da-e63113e88e45'],
          ['8ee8db78-e855-11ef-ae13-bc24117fdf42', '5af7ca98-a057-11f1-b2e3-e63113e88e45'],
          ['9dbc8e4c-e855-11ef-aca0-bc24117fdf42', '5af7e2b2-a057-11f1-aafd-e63113e88e45'],
          ['f6ccf70a-00b6-11f0-aee0-bc24117fdf42', '5af94864-a057-11f1-a9cc-e63113e88e45'],
          ['a06d4f6a-b903-11ed-ba51-4217ca18cd30', '5ad83e26-a057-11f1-9c32-e63113e88e45'],
          ['7ac036aa-34ad-11f1-9911-bc2411422144', '5ab88702-a057-11f1-ab2b-e63113e88e45'],
          ['63972abc-be0a-11ef-a8f4-bc24117fdf42', '5abc5e86-a057-11f1-8200-e63113e88e45'],
          ['e28c17d0-4a04-11ef-a4f2-f6cca806884c', '5ab8da18-a057-11f1-b576-e63113e88e45'],
          ['94659b58-34ae-11f1-a638-bc2411422144', '5b13630c-a057-11f1-b0c6-e63113e88e45'],
          ['f7e29370-4405-11ef-896f-f6cca806884c', '5aba8822-a057-11f1-bd8b-e63113e88e45'],
          ['215b7b42-e2e2-11ef-a6bc-024254c499cd', '5aba1928-a057-11f1-92e0-e63113e88e45'],
          ['d52b454a-5b5d-11f1-b745-8a3318f7d826', '5b146d4c-a057-11f1-b1c6-e63113e88e45'],
          ['d52c3298-5b5d-11f1-b4c2-8a3318f7d826', '5b1485d4-a057-11f1-8bba-e63113e88e45'],
          ['d52c1722-5b5d-11f1-9e6f-8a3318f7d826', '5ab88702-a057-11f1-ab2b-e63113e88e45'],
          ['d52c0818-5b5d-11f1-a9cd-8a3318f7d826', '5ab8ffd4-a057-11f1-96ef-e63113e88e45'],
          ['d52c2528-5b5d-11f1-86ef-8a3318f7d826', '5ab8c5c8-a057-11f1-8a98-e63113e88e45'],
          ['84a0bff0-c54f-11f0-8496-5a54e0743c62', '5ab88702-a057-11f1-ab2b-e63113e88e45'],
          ['1ad70e1c-354f-11ef-aed6-0242d1a6ff54', '5ab8ffd4-a057-11f1-96ef-e63113e88e45'],
          ['1ad70228-354f-11ef-9663-0242d1a6ff54', '5ab8c5c8-a057-11f1-8a98-e63113e88e45'],
          ['1ad724a6-354f-11ef-87d5-0242d1a6ff54', '5abcf80a-a057-11f1-b52e-e63113e88e45'],
          ['1ad6ef72-354f-11ef-a65a-0242d1a6ff54', '5af11496-a057-11f1-88ca-e63113e88e45'],
          ['31319b70-556e-11ef-8873-42ceff40c0f9', '5af142d6-a057-11f1-aba8-e63113e88e45'],
          ['1ad719b6-354f-11ef-9fc2-0242d1a6ff54', '5ad5529c-a057-11f1-87f4-e63113e88e45'],
          ['48abca40-2791-11f1-853b-16d0caa4fd61', '5ad48e5c-a057-11f1-9202-e63113e88e45'],
          ['84946696-ec8c-11ec-a977-222560b74e07', '5abd5d68-a057-11f1-a35c-e63113e88e45'],
          ['29f6fd80-5f4a-11ed-a07a-222560b74e07', '5ab88702-a057-11f1-ab2b-e63113e88e45'],
          ['2181b064-5c2a-11ed-be64-222560b74e07', '5ad411e8-a057-11f1-a5e3-e63113e88e45'],
          ['84947e9c-ec8c-11ec-9f17-222560b74e07', '5abd7834-a057-11f1-b1f9-e63113e88e45'],
          ['40585610-2791-11f1-9b5d-16d0caa4fd61', '5aba1928-a057-11f1-92e0-e63113e88e45'],
          ['70fb2026-cc66-11f0-8af9-5a54e0743c62', '5b0c05d0-a057-11f1-bbf8-e63113e88e45'],
          ['96718254-f14b-11ec-8e4c-ce9ac48e4e9d', '5ac1db40-a057-11f1-8868-e63113e88e45'],
          ['3073796a-7a28-11ed-8199-4217ca18cd30', '5ad48e5c-a057-11f1-9202-e63113e88e45'],
          ['039d88ba-ec8c-11ec-90f1-ce9ac48e4e9d', '5aba03b6-a057-11f1-827c-e63113e88e45'],
          ['3073b6aa-7a28-11ed-abca-4217ca18cd30', '5ab88702-a057-11f1-ab2b-e63113e88e45'],
          ['3073a5f2-7a28-11ed-9103-4217ca18cd30', '5ab8ffd4-a057-11f1-96ef-e63113e88e45'],
          ['3073af5c-7a28-11ed-9ac0-4217ca18cd30', '5abc75a6-a057-11f1-84a0-e63113e88e45'],
          ['307387c0-7a28-11ed-8ee8-4217ca18cd30', '5ad4a248-a057-11f1-9461-e63113e88e45'],
          ['039da570-ec8c-11ec-a5dd-ce9ac48e4e9d', '5abcf80a-a057-11f1-b52e-e63113e88e45'],
          ['79d4b0b0-7a28-11ed-93da-0242ea297dde', '5ad4b8aa-a057-11f1-a922-e63113e88e45'],
          ['79d4cb0e-7a28-11ed-ab6e-0242ea297dde', '5ad4ccb4-a057-11f1-a180-e63113e88e45'],
          ['79d4e396-7a28-11ed-91e6-0242ea297dde', '5aba8822-a057-11f1-bd8b-e63113e88e45'],
          ['79d4fa0c-7a28-11ed-a970-0242ea297dde', '5ac15d46-a057-11f1-8188-e63113e88e45'],
          ['79d51a3c-7a28-11ed-b3de-0242ea297dde', '5aba1928-a057-11f1-92e0-e63113e88e45'],
          ['79d5347c-7a28-11ed-a518-0242ea297dde', '5ad4e438-a057-11f1-b769-e63113e88e45'],
          ['79d5856c-7a28-11ed-84f6-0242ea297dde', '5ad52844-a057-11f1-9cb8-e63113e88e45'],
          ['8efc1cb2-7a28-11ed-8b7e-0242ea297dde', '5ad53d3e-a057-11f1-8df7-e63113e88e45'],
          ['79d548e0-7a28-11ed-9600-0242ea297dde', '5ad4fd1a-a057-11f1-b4b2-e63113e88e45'],
          ['79d55aba-7a28-11ed-87fb-0242ea297dde', '5ad513ae-a057-11f1-a1b7-e63113e88e45'],
          ['8efc3670-7a28-11ed-b4e3-0242ea297dde', '5ad5529c-a057-11f1-87f4-e63113e88e45'],
          ['c08e62fe-a85d-11ed-982b-ce9ac48e4e9d', '5ad64ca6-a057-11f1-a511-e63113e88e45'],
          ['af4093f0-ebd4-11ec-96ca-4217ca18cd30', '5ab89c4c-a057-11f1-8378-e63113e88e45'],
          ['baafa6e2-ec8b-11ec-9f35-ce9ac48e4e9d', '5ab8c5c8-a057-11f1-8a98-e63113e88e45'],
          ['3827fe54-ec8b-11ec-8cc3-ce9ac48e4e9d', '5abc45ea-a057-11f1-9820-e63113e88e45'],
          ['382812d6-ec8b-11ec-84a9-ce9ac48e4e9d', '5ab8ffd4-a057-11f1-96ef-e63113e88e45'],
          ['382821cc-ec8b-11ec-a63f-ce9ac48e4e9d', '5abc5e86-a057-11f1-8200-e63113e88e45'],
          ['38282fc8-ec8b-11ec-839b-ce9ac48e4e9d', '5abc75a6-a057-11f1-84a0-e63113e88e45'],
          ['38283e1e-ec8b-11ec-99d7-ce9ac48e4e9d', '5abc8ec4-a057-11f1-bd45-e63113e88e45'],
          ['bcd9a156-5bdf-11ee-87d5-ce9ac48e4e9d', '5adfee0a-a057-11f1-be9b-e63113e88e45'],
          ['96591e44-5bdf-11ee-8c7c-0242d041ae4b', '5adfab7a-a057-11f1-b1c5-e63113e88e45'],
          ['068b83ea-6dbb-11ee-b1ad-ce9ac48e4e9d', '5ae16190-a057-11f1-8db8-e63113e88e45'],
          ['14d6a6a0-6dbb-11ee-af56-ce9ac48e4e9d', '5ae17bda-a057-11f1-9327-e63113e88e45'],
          ['22b25800-6dbb-11ee-90c3-4217ca18cd30', '5ae1991c-a057-11f1-811c-e63113e88e45'],
          ['2cea1650-6dbb-11ee-a543-02425069021c', '5ae1b000-a057-11f1-adcc-e63113e88e45'],
          ['33e9dc24-6dbb-11ee-ade0-4217ca18cd30', '5ae1c798-a057-11f1-96dc-e63113e88e45'],
          ['3b5dbb06-6dbb-11ee-9777-02425069021c', '5ae1de86-a057-11f1-be30-e63113e88e45'],
          ['53a950f8-6dbb-11ee-b5eb-4217ca18cd30', '5ae22544-a057-11f1-aaf4-e63113e88e45'],
          ['42e974be-6dbb-11ee-9498-4217ca18cd30', '5ae1f6f0-a057-11f1-a0e0-e63113e88e45'],
          ['497c50a8-6dbb-11ee-bf3d-ce9ac48e4e9d', '5ae20f78-a057-11f1-a6fc-e63113e88e45'],
          ['5cddb8d0-6dbb-11ee-b3d8-ce9ac48e4e9d', '5ae23d2c-a057-11f1-9d9f-e63113e88e45'],
          ['6eca69e4-6dbb-11ee-94d5-02425069021c', '5ae25686-a057-11f1-abf0-e63113e88e45'],
          ['75d0134c-6dbb-11ee-9f62-4217ca18cd30', '5ae26d9c-a057-11f1-a246-e63113e88e45'],
          ['80f1e264-6dbb-11ee-ac62-ce9ac48e4e9d', '5ae285c0-a057-11f1-922b-e63113e88e45'],
          ['9225c366-6dbb-11ee-95f6-02425069021c', '5ae29c72-a057-11f1-837b-e63113e88e45'],
          ['7d209330-71f9-11f0-8668-bc2411422144', '5aff930e-a057-11f1-85f6-e63113e88e45'],
          ['98160ca4-6dbb-11ee-8b5b-02425069021c', '5ae2b46e-a057-11f1-8520-e63113e88e45'],
          ['9ca4b130-6dbb-11ee-aba2-02425069021c', '5ae2cdc8-a057-11f1-a98d-e63113e88e45'],
          ['a48322a6-6dbb-11ee-a9d1-4217ca18cd30', '5ae2e39e-a057-11f1-a0f0-e63113e88e45'],
          ['abc8ef5a-6dbb-11ee-a33d-02425069021c', '5ae2f97e-a057-11f1-8d84-e63113e88e45'],
          ['b3b791ee-6dbb-11ee-a848-02425069021c', '5ae31224-a057-11f1-9f50-e63113e88e45'],
          ['b919cc06-6dbb-11ee-8967-ce9ac48e4e9d', '5ae328ae-a057-11f1-b74f-e63113e88e45'],
          ['bf7c1338-6dbb-11ee-8be6-4217ca18cd30', '5ae33df8-a057-11f1-94d4-e63113e88e45'],
          ['c88d08ec-6dbb-11ee-abd4-4217ca18cd30', '5ae35374-a057-11f1-bdbb-e63113e88e45'],
          ['cfd2baa2-6dbb-11ee-8928-ce9ac48e4e9d', '5ae36a08-a057-11f1-9a7d-e63113e88e45'],
          ['d6ba0ff0-6dbb-11ee-9ebf-02425069021c', '5ae37ebc-a057-11f1-9829-e63113e88e45'],
          ['dc9c0cca-6dbb-11ee-a5b3-ce9ac48e4e9d', '5ae3951e-a057-11f1-b1a2-e63113e88e45'],
          ['eacdf10a-6dbb-11ee-ac39-ce9ac48e4e9d', '5ae3aeaa-a057-11f1-8a72-e63113e88e45'],
          ['f22b5d48-6dbb-11ee-a132-4217ca18cd30', '5ae3cb60-a057-11f1-8caa-e63113e88e45'],
          ['f82de9ae-6dbb-11ee-ab1c-4217ca18cd30', '5ae3e230-a057-11f1-aab1-e63113e88e45'],
          ['6c48e2fe-3c9e-11f1-8d41-3e37ce0a703b', '5b13778e-a057-11f1-afea-e63113e88e45'],
          ['54dd6d20-ec88-11ec-829f-4217ca18cd30', '5abbbeea-a057-11f1-bc05-e63113e88e45'],
          ['dc4d944c-b1ef-11ed-85a5-4217ca18cd30', '5ab8c5c8-a057-11f1-8a98-e63113e88e45'],
          ['7cedc58e-3c9e-11f1-b634-3e37ce0a703b', '5b138c1a-a057-11f1-91b8-e63113e88e45'],
          ['d9bf47da-6a58-11ee-b897-4217ca18cd30', '5abc5e86-a057-11f1-8200-e63113e88e45'],
          ['54de1824-ec88-11ec-ac22-4217ca18cd30', '5abbd704-a057-11f1-a1d5-e63113e88e45'],
          ['d9ed5ab8-7abc-11f1-9544-bc24117fdf42', '5aba03b6-a057-11f1-827c-e63113e88e45'],
          ['1e85e4b0-7ac2-11f1-b98e-bc24117fdf42', '5b15389e-a057-11f1-bc83-e63113e88e45'],
          ['7a434e5e-1235-11ed-a58d-4217ca18cd30', '5ab8ffd4-a057-11f1-96ef-e63113e88e45'],
          ['1a953e50-7b7b-11f1-b2e9-bc24117fdf42', '5b156990-a057-11f1-bd7a-e63113e88e45'],
          ['6d0d3122-c40b-11ed-bf91-02426a51ec4a', '5abc5e86-a057-11f1-8200-e63113e88e45'],
          ['af6725ac-1234-11ed-b9e8-ce9ac48e4e9d', '5aba1928-a057-11f1-92e0-e63113e88e45'],
          ['6910020c-c40b-11ed-bf54-ce9ac48e4e9d', '5ad513ae-a057-11f1-a1b7-e63113e88e45'],
          ['6a14f3c0-7e27-11ee-9b58-ce9ac48e4e9d', '5ae67644-a057-11f1-a263-e63113e88e45'],
          ['510799f8-e517-11ee-a558-ce9ac48e4e9d', '5aee3690-a057-11f1-b8ff-e63113e88e45'],
          ['51074ab6-e517-11ee-aff9-ce9ac48e4e9d', '5aedb03a-a057-11f1-9f6f-e63113e88e45'],
          ['51076db6-e517-11ee-85ee-ce9ac48e4e9d', '5aede744-a057-11f1-a257-e63113e88e45'],
          ['51075cea-e517-11ee-95ed-ce9ac48e4e9d', '5aedc8ea-a057-11f1-99cc-e63113e88e45'],
          ['5107ab5a-e517-11ee-b7d8-ce9ac48e4e9d', '5aee4fb8-a057-11f1-893a-e63113e88e45'],
          ['51077a68-e517-11ee-be76-ce9ac48e4e9d', '5aee01a2-a057-11f1-a40f-e63113e88e45'],
          ['51078b52-e517-11ee-a8e9-ce9ac48e4e9d', '5aee1d0e-a057-11f1-8e15-e63113e88e45'],
          ['6a14e308-7e27-11ee-8ba4-ce9ac48e4e9d', '5ae65c40-a057-11f1-9d8e-e63113e88e45'],
          ['460551ac-ec86-11ec-863c-222560b74e07', '5abaef9c-a057-11f1-8191-e63113e88e45'],
          ['46056296-ec86-11ec-9fc0-222560b74e07', '5abb1378-a057-11f1-890a-e63113e88e45'],
          ['4605706a-ec86-11ec-beff-222560b74e07', '5abb3b00-a057-11f1-bb8c-e63113e88e45'],
          ['692d1eb2-e30d-11ec-8b41-4217ca18cd30', '5ab7481a-a057-11f1-9bb5-e63113e88e45'],
          ['6c2c06d6-c98b-11ed-8077-02426a51ec4a', '5ad48e5c-a057-11f1-9202-e63113e88e45'],
          ['53551d00-c98b-11ed-9057-02426a51ec4a', '5aba03b6-a057-11f1-827c-e63113e88e45'],
          ['060c1a68-ca75-11ed-8d64-ce9ac48e4e9d', '5ad974da-a057-11f1-8ccb-e63113e88e45'],
          ['fd8193c6-7b6c-11f1-a32c-bc24117fdf42', '5b15548c-a057-11f1-8199-e63113e88e45'],
          ['80e209fe-c98b-11ed-b107-4217ca18cd30', '5ab8c5c8-a057-11f1-8a98-e63113e88e45'],
          ['a68830c0-c98b-11ed-a96e-02426a51ec4a', '5ad95b80-a057-11f1-8b8c-e63113e88e45'],
          ['8675c392-c98b-11ed-9d7e-4217ca18cd30', '5ad94460-a057-11f1-adcb-e63113e88e45'],
          ['a701660c-ec28-11ec-abc7-222560b74e07', '5ab9ad6c-a057-11f1-adf1-e63113e88e45'],
          ['d8bba65a-ec85-11ec-a633-ce9ac48e4e9d', '5aba8822-a057-11f1-bd8b-e63113e88e45'],
          ['8aaf8aa6-c98b-11ed-b114-02426a51ec4a', '5ac15d46-a057-11f1-8188-e63113e88e45'],
          ['4dc43452-c98b-11ed-ac4f-02426a51ec4a', '5ad52844-a057-11f1-9cb8-e63113e88e45'],
          ['9e22b28e-c98b-11ed-b208-4217ca18cd30', '5ad53d3e-a057-11f1-8df7-e63113e88e45'],
          ['956eed6a-c98b-11ed-bd12-4217ca18cd30', '5ad5529c-a057-11f1-87f4-e63113e88e45'],
          ['c5929dbe-81e5-11f1-b0b9-d23673154e14', '5ab8ed00-a057-11f1-91b9-e63113e88e45'],
          ['efb66f4e-5b88-11ed-bf43-222560b74e07', '5ad34d4e-a057-11f1-929f-e63113e88e45'],
          ['efb70300-5b88-11ed-ae54-222560b74e07', '5ad3e0a6-a057-11f1-a0f8-e63113e88e45'],
          ['1b69ab38-6a57-11ed-b08e-4217ca18cd30', '5ad479bc-a057-11f1-b1b8-e63113e88e45'],
          ['efb6f2e8-5b88-11ed-8cc8-222560b74e07', '5ad3cce2-a057-11f1-97d8-e63113e88e45'],
          ['a0f77de8-b9d5-11ed-90df-ce9ac48e4e9d', '5ad8983a-a057-11f1-8aec-e63113e88e45'],
          ['efb6816e-5b88-11ed-8ddf-222560b74e07', '5ad363e2-a057-11f1-aa94-e63113e88e45'],
          ['efb69316-5b88-11ed-ac27-222560b74e07', '5ad37792-a057-11f1-b166-e63113e88e45'],
          ['efb6babc-5b88-11ed-82a2-222560b74e07', '5ad38c1e-a057-11f1-a7d2-e63113e88e45'],
          ['efb6c778-5b88-11ed-8b92-222560b74e07', '5ad3a000-a057-11f1-97ee-e63113e88e45'],
          ['efb6d952-5b88-11ed-9c32-222560b74e07', '5ad3b81a-a057-11f1-85f4-e63113e88e45'],
          ['7e3f42b4-7f88-11f1-a289-d23673154e14', '5b15a77a-a057-11f1-bfea-e63113e88e45'],
          ['efb71606-5b88-11ed-ae17-222560b74e07', '5ad3f5be-a057-11f1-a2f5-e63113e88e45'],
          ['53b6da62-0105-11ed-9d0a-ce9ac48e4e9d', '5ac2e972-a057-11f1-be73-e63113e88e45'],
          ['53b6ca90-0105-11ed-9be8-ce9ac48e4e9d', '5ac2c9ce-a057-11f1-af34-e63113e88e45'],
          ['53b6fb28-0105-11ed-b449-ce9ac48e4e9d', '5ac326ee-a057-11f1-9a3f-e63113e88e45'],
          ['53b70b2c-0105-11ed-b6a8-ce9ac48e4e9d', '5ac33ecc-a057-11f1-ba9c-e63113e88e45'],
          ['53b6a092-0105-11ed-8251-ce9ac48e4e9d', '5ac2b2ae-a057-11f1-9f7a-e63113e88e45'],
          ['53b6ec8c-0105-11ed-b585-ce9ac48e4e9d', '5ac30c18-a057-11f1-a8ad-e63113e88e45'],
          ['d05896ce-39ad-11ed-92b7-4217ca18cd30', '5ac932a0-a057-11f1-9780-e63113e88e45'],
          ['b11c3c0e-2231-11ed-b72f-222560b74e07', '5ac6fa6c-a057-11f1-93c3-e63113e88e45'],
          ['b11c4c94-2231-11ed-bc33-222560b74e07', '5ac71c22-a057-11f1-a83c-e63113e88e45'],
          ['d05881ca-39ad-11ed-bdbc-4217ca18cd30', '5ac90dc0-a057-11f1-84e6-e63113e88e45'],
          ['d058c090-39ad-11ed-ab24-4217ca18cd30', '5ac98c46-a057-11f1-bca6-e63113e88e45'],
          ['b11c67ec-2231-11ed-8960-222560b74e07', '5ac75aa2-a057-11f1-a088-e63113e88e45'],
          ['d058b6e0-39ad-11ed-a0c6-4217ca18cd30', '5ac96ca2-a057-11f1-98f9-e63113e88e45'],
          ['d058cfe0-39ad-11ed-b241-4217ca18cd30', '5ac9aece-a057-11f1-b1ad-e63113e88e45'],
          ['d058acf4-39ad-11ed-91c1-4217ca18cd30', '5ac94e0c-a057-11f1-a7bd-e63113e88e45'],
          ['b11c5ac2-2231-11ed-bba6-222560b74e07', '5ac73c3e-a057-11f1-b6c1-e63113e88e45'],
          ['906ec276-3274-11ed-8124-222560b74e07', '5ac80b00-a057-11f1-877a-e63113e88e45'],
          ['7896b5ce-6a54-11ed-8fec-0242ea297dde', '5ad46422-a057-11f1-8f03-e63113e88e45'],
          ['1f99d78c-6a4d-11ed-a715-ce9ac48e4e9d', '5ad4282c-a057-11f1-9e19-e63113e88e45'],
          ['2aa08536-6a4d-11ed-babe-4217ca18cd30', '5ad44f1e-a057-11f1-8801-e63113e88e45'],
          ['2aa075dc-6a4d-11ed-b781-4217ca18cd30', '5ad43ae2-a057-11f1-9187-e63113e88e45'],
          ['ca03125a-a287-11ef-b402-aa28198788c5', '5af57aae-a057-11f1-9aa9-e63113e88e45'],
          ['a3b6944e-9604-11ef-99c7-bc24117fdf42', '5af4ca78-a057-11f1-ad7f-e63113e88e45'],
          ['8f5570d8-9604-11ef-9d81-bc24117fdf42', '5af4a39a-a057-11f1-948b-e63113e88e45'],
          ['228d3aae-cbec-11ee-8e0f-0242feafac95', '5aed03c4-a057-11f1-ba63-e63113e88e45'],
          ['8f54c9c6-9604-11ef-b1db-bc24117fdf42', '5af47a0a-a057-11f1-990f-e63113e88e45'],
          ['1cfcdbf4-e481-11ef-ab96-024254c499cd', '5af75e14-a057-11f1-8e04-e63113e88e45'],
          ['228d1d58-cbec-11ee-9211-0242feafac95', '5aecca44-a057-11f1-b008-e63113e88e45'],
          ['ada2db00-e2f5-11ef-84ca-024254c499cd', '5af7497e-a057-11f1-925b-e63113e88e45'],
          ['061f6ee6-0949-11f0-898f-bc2411730653', '5af97794-a057-11f1-a294-e63113e88e45'],
          ['aec9f7d4-2bf2-11f0-bdf2-bc24117fdf42', '5afaba6e-a057-11f1-b6ff-e63113e88e45'],
          ['a3b71f5e-9604-11ef-9ed2-bc24117fdf42', '5af4deaa-a057-11f1-ac1d-e63113e88e45'],
          ['8f555bca-9604-11ef-a974-bc24117fdf42', '5af48f86-a057-11f1-872c-e63113e88e45'],
          ['228d4742-cbec-11ee-8136-0242feafac95', '5aed1aa8-a057-11f1-96de-e63113e88e45'],
          ['98acc334-9604-11ef-860b-bc24117fdf42', '5af4b70e-a057-11f1-88b2-e63113e88e45'],
          ['228d2d84-cbec-11ee-b96b-0242feafac95', '5aece2a4-a057-11f1-b820-e63113e88e45'],
          ['849629c2-496b-11ed-9208-ce9ac48e4e9d', '5acac69c-a057-11f1-b6ab-e63113e88e45'],
          ['51b43ab4-b9c1-11ed-9e28-4217ca18cd30', '5ad882be-a057-11f1-8dfd-e63113e88e45'],
          ['32623ba6-90ff-11ed-a80d-ce9ac48e4e9d', '5ad617d6-a057-11f1-8273-e63113e88e45'],
          ['3846e63e-90ff-11ed-908e-0242ea297dde', '5ad62e4c-a057-11f1-8df3-e63113e88e45'],
          ['2a745c04-b9c1-11ed-8600-ce9ac48e4e9d', '5ad854a6-a057-11f1-9946-e63113e88e45'],
          ['be4f4af4-4067-11f0-ad05-bc24117fdf42', '5afbab54-a057-11f1-9e16-e63113e88e45'],
          ['3255becc-b9c1-11ed-a663-024254d65211', '5ad86b94-a057-11f1-b1ef-e63113e88e45'],
          ['b538303c-90fd-11ed-a0c0-0242ea297dde', '5ad5f044-a057-11f1-a9e4-e63113e88e45'],
          ['a3c5cb7a-90fd-11ed-b86a-0242ea297dde', '5ad5c088-a057-11f1-95f4-e63113e88e45'],
          ['aea70bd0-90fd-11ed-8dab-ce9ac48e4e9d', '5ad5d9ce-a057-11f1-a6df-e63113e88e45'],
          ['298ce148-90ff-11ed-8141-0242ea297dde', '5ad60426-a057-11f1-af05-e63113e88e45'],
          ['3bb56282-7894-11ee-8d78-ce9ac48e4e9d', '5ae3fac2-a057-11f1-b9a6-e63113e88e45'],
          ['9d0ef94a-13e2-11f0-8deb-bc2411730653', '5afa57cc-a057-11f1-8014-e63113e88e45'],
          ['56835e82-abe6-11ed-9a13-ce9ac48e4e9d', '5ad6e440-a057-11f1-b0af-e63113e88e45'],
          ['b4f28d2c-abe5-11ed-b078-024254d65211', '5ad6cb40-a057-11f1-b63c-e63113e88e45'],
          ['aa1d7178-abe5-11ed-aede-4217ca18cd30', '5ad6b268-a057-11f1-9d7e-e63113e88e45'],
          ['fff8c194-8b4b-11f1-b7c9-bc24117fdf42', '5aba03b6-a057-11f1-827c-e63113e88e45'],
          ['a00cd5f8-8b56-11f1-bffb-bc24117fdf42', '5b146d4c-a057-11f1-b1c6-e63113e88e45'],
          ['ee596c98-8b52-11f1-bf6a-bc24117fdf42', '5ab88702-a057-11f1-ab2b-e63113e88e45'],
          ['fff80db2-8b4b-11f1-9d3c-bc24117fdf42', '5ab8ffd4-a057-11f1-96ef-e63113e88e45'],
          ['fff8d990-8b4b-11f1-822d-bc24117fdf42', '5abc75a6-a057-11f1-84a0-e63113e88e45'],
          ['fff8f11e-8b4b-11f1-871f-bc24117fdf42', '5abcf80a-a057-11f1-b52e-e63113e88e45'],
          ['fff9049c-8b4b-11f1-98e5-bc24117fdf42', '5b18db34-a057-11f1-9785-e63113e88e45'],
          ['fff91626-8b4b-11f1-b2cd-bc24117fdf42', '5ad94460-a057-11f1-adcb-e63113e88e45'],
          ['fff9281e-8b4b-11f1-b3f9-bc24117fdf42', '5b18f2b8-a057-11f1-b82e-e63113e88e45'],
          ['cfef0f96-da99-11ed-aadf-4217ca18cd30', '5ada088c-a057-11f1-9ed5-e63113e88e45'],
          ['cfef1720-da99-11ed-88da-4217ca18cd30', '5ada1eee-a057-11f1-b064-e63113e88e45'],
          ['cfef074e-da99-11ed-bd92-4217ca18cd30', '5ad9ef3c-a057-11f1-a1db-e63113e88e45'],
          ['cfeefbc8-da99-11ed-84ef-4217ca18cd30', '5ad9d9b6-a057-11f1-b0ae-e63113e88e45'],
          ['4a2284fa-5459-11f1-910c-222560b74e07', '5b13b5b4-a057-11f1-8229-e63113e88e45'],
          ['72d3f6ee-5352-11f0-9a95-222560b74e07', '5ab88702-a057-11f1-ab2b-e63113e88e45'],
          ['c66b925a-54f3-11f1-b0ad-fec1ce27a635', '5b13ca54-a057-11f1-a91f-e63113e88e45'],
          ['72d32aac-5352-11f0-9889-222560b74e07', '5afd4176-a057-11f1-8b52-e63113e88e45'],
          ['58322c84-5352-11f0-9737-222560b74e07', '5afd25a6-a057-11f1-ab64-e63113e88e45'],
          ['bbeace20-54f5-11f1-91c7-fec1ce27a635', '5aba8822-a057-11f1-bd8b-e63113e88e45'],
          ['bc00c22e-0436-11ee-b5f2-02420e37ec7a', '5adaa058-a057-11f1-b31b-e63113e88e45'],
          ['b52f46a0-0436-11ee-b471-4217ca18cd30', '5ada8b18-a057-11f1-8455-e63113e88e45'],
          ['1d2aac46-1502-11ee-8d7c-02420d25dd36', '5adc1136-a057-11f1-9dae-e63113e88e45'],
          ['1d2a99ae-1502-11ee-bfee-02420d25dd36', '5adbf7e6-a057-11f1-bd8c-e63113e88e45'],
          ['b19de4dc-1a35-11ee-abd8-4217ca18cd30', '5adc81de-a057-11f1-8ff7-e63113e88e45'],
          ['b19df030-1a35-11ee-af33-4217ca18cd30', '5adbf7e6-a057-11f1-bd8c-e63113e88e45'],
          ['bbb576a8-a456-11f0-8e9c-bc24117fdf42', '5b00af50-a057-11f1-b2fe-e63113e88e45'],
          ['88843260-a456-11f0-91c5-bc24117fdf42', '5b0099c0-a057-11f1-9fcc-e63113e88e45'],
          ['bbb62300-a456-11f0-87f5-bc24117fdf42', '5ac326ee-a057-11f1-9a3f-e63113e88e45'],
          ['6c1a8750-a456-11f0-8049-bc24117fdf42', '5ac2b2ae-a057-11f1-9f7a-e63113e88e45'],
          ['51c2df0a-2913-11f1-8fb0-bc2411730653', '5b134bf6-a057-11f1-979a-e63113e88e45'],
          ['d3f12368-117d-11f1-90f0-4217ca18cd30', '5b12977e-a057-11f1-a53b-e63113e88e45'],
          ['6410275a-7e6c-11f0-bc6d-bc2411422144', '5affaa24-a057-11f1-88fa-e63113e88e45'],
          ['85683e80-1181-11f1-aa19-4217ca18cd30', '5b12b146-a057-11f1-8e9f-e63113e88e45'],
          ['018b3750-8272-11f0-94de-0242b2b0566e', '5affc266-a057-11f1-872a-e63113e88e45'],
          ['58b75278-3fa3-11f0-9a83-bc24117fdf42', '5afb7b02-a057-11f1-913c-e63113e88e45'],
          ['b1e31248-f2e4-11f0-ae5b-ce9ac48e4e9d', '5b0df214-a057-11f1-b832-e63113e88e45'],
          ['9eae0d54-7b92-11f1-a7ad-bc24117fdf42', '5b157e6c-a057-11f1-99cf-e63113e88e45'],
          ['e373ffb4-6baf-11f0-bf8f-86f987394d46', '5aff4ec6-a057-11f1-93b9-e63113e88e45'],
          ['383ac7f6-fc53-11f0-9cd0-ce9ac48e4e9d', '5b0e08a8-a057-11f1-8496-e63113e88e45'],
          ['383b6dbe-fc53-11f0-8869-ce9ac48e4e9d', '5b0e1c80-a057-11f1-9414-e63113e88e45'],
          ['d7b6a4d4-ceaf-11f0-a4e3-bc24117fdf42', '5b0c1aac-a057-11f1-b90d-e63113e88e45'],
          ['383b824a-fc53-11f0-a812-ce9ac48e4e9d', '5b0e3152-a057-11f1-80ca-e63113e88e45'],
          ['383b97ee-fc53-11f0-af75-ce9ac48e4e9d', '5b0e44f8-a057-11f1-a915-e63113e88e45'],
          ['c0aa1c9c-b43f-11f0-a152-02425e7135e4', '5b02314a-a057-11f1-84a7-e63113e88e45'],
          ['5bf9d5fa-3fa3-11f0-9765-bc24117fdf42', '5afb933a-a057-11f1-b2e8-e63113e88e45'],
          ['736624ca-6598-11f1-b794-fe6c6ee16cdc', '5b149c72-a057-11f1-a554-e63113e88e45'],
          ['27268caa-6899-11f0-a1d4-02425a152513', '5aff0ff6-a057-11f1-8c80-e63113e88e45'],
          ['383bac8e-fc53-11f0-ad60-ce9ac48e4e9d', '5b0e6230-a057-11f1-9e78-e63113e88e45'],
          ['383bc200-fc53-11f0-819b-ce9ac48e4e9d', '5b0e76bc-a057-11f1-97e2-e63113e88e45'],
          ['2153acb6-8272-11f0-a370-0242b2b0566e', '5affd83c-a057-11f1-8e11-e63113e88e45'],
          ['3c8b13dc-7b93-11f1-9d01-bc24117fdf42', '5b159280-a057-11f1-b1c0-e63113e88e45'],
          ['509d03d0-3fa3-11f0-a882-bc24117fdf42', '5afb49fc-a057-11f1-a7f2-e63113e88e45'],
          ['27267b66-6899-11f0-86f2-02425a152513', '5afefcaa-a057-11f1-be3a-e63113e88e45'],
          ['e72ae43e-6898-11f0-a79d-02425a152513', '5afed50e-a057-11f1-9d6f-e63113e88e45'],
          ['45dcd946-c141-11f0-bf08-02425e7135e4', '5b03d770-a057-11f1-a098-e63113e88e45'],
          ['2310226e-93d9-11f0-bad1-bc2411422144', '5b000d20-a057-11f1-bc85-e63113e88e45'],
          ['55733320-3fa3-11f0-b614-bc24117fdf42', '5afb6388-a057-11f1-8f53-e63113e88e45'],
          ['48aba54a-13c5-11f1-ae71-024250e21381', '5b12cb40-a057-11f1-8b1d-e63113e88e45'],
          ['383bd2e0-fc53-11f0-9aec-ce9ac48e4e9d', '5b0e8c60-a057-11f1-bf6c-e63113e88e45'],
          ['3b856cda-3fa3-11f0-baee-bc24117fdf42', '5afb2f3a-a057-11f1-a303-e63113e88e45'],
          ['383be758-fc53-11f0-9776-ce9ac48e4e9d', '5b0ea150-a057-11f1-adf3-e63113e88e45'],
          ['383bf766-fc53-11f0-aff2-ce9ac48e4e9d', '5b0eb8b6-a057-11f1-8c00-e63113e88e45'],
          ['6072a192-b3f8-11f0-b8c4-02425e7135e4', '5b021cf0-a057-11f1-a2d9-e63113e88e45'],
          ['2726a3fc-6899-11f0-91ac-02425a152513', '5aff240a-a057-11f1-87a2-e63113e88e45'],
          ['383c06ca-fc53-11f0-a13b-ce9ac48e4e9d', '5b0ed15c-a057-11f1-97d4-e63113e88e45'],
          ['383c15de-fc53-11f0-a07b-ce9ac48e4e9d', '5b0ee840-a057-11f1-abae-e63113e88e45'],
          ['272668f6-6899-11f0-a653-02425a152513', '5aec0550-a057-11f1-a5bf-e63113e88e45'],
          ['43f8324c-6bb5-11f0-9082-86f987394d46', '5aff65c8-a057-11f1-8de1-e63113e88e45'],
          ['2725dcd8-6899-11f0-8a25-02425a152513', '5afee8a0-a057-11f1-b0a6-e63113e88e45'],
          ['e5269488-b43f-11f0-aaa6-02425e7135e4', '5b02472a-a057-11f1-8514-e63113e88e45'],
          ['e292319e-9943-11f0-adf3-bc2411422144', '5b0038c2-a057-11f1-bde7-e63113e88e45'],
          ['0919fdc2-13c6-11f1-b1d5-024250e21381', '5b12dfd6-a057-11f1-809a-e63113e88e45'],
          ['7516f2ba-1060-11f0-96c7-bc2411730653', '5ae4952c-a057-11f1-978d-e63113e88e45'],
          ['75177834-1060-11f0-b2a5-bc2411730653', '5ae4ab02-a057-11f1-879a-e63113e88e45'],
          ['112a1a62-1541-11f0-b0e1-bc24117fdf42', '5aec3d18-a057-11f1-aa77-e63113e88e45'],
          ['751764fc-1060-11f0-b9f7-bc2411730653', '5afa392c-a057-11f1-9549-e63113e88e45'],
          ['1d95bebe-1541-11f0-a7d6-bc24117fdf42', '5ae51ca4-a057-11f1-8434-e63113e88e45'],
          ['28d4fb78-1541-11f0-a900-bc24117fdf42', '5afa8972-a057-11f1-bbbe-e63113e88e45'],
          ['6750860a-3474-11ef-94a4-024284af7bf4', '5af05ad8-a057-11f1-9beb-e63113e88e45'],
          ['cc48a874-7d3d-11ee-bbd5-4217ca18cd30', '5ae46976-a057-11f1-a008-e63113e88e45'],
          ['da263902-e6bf-11ee-ad36-222560b74e07', '5aee691c-a057-11f1-9a6f-e63113e88e45'],
          ['cc48b346-7d3d-11ee-88b6-4217ca18cd30', '5ae47f88-a057-11f1-8ca0-e63113e88e45'],
          ['da6d5402-48e1-11ef-b1c8-ce9ac48e4e9d', '5af12e22-a057-11f1-a184-e63113e88e45'],
          ['cc48bb20-7d3d-11ee-a358-4217ca18cd30', '5ae4952c-a057-11f1-978d-e63113e88e45'],
          ['cc48c2dc-7d3d-11ee-988f-4217ca18cd30', '5ae4ab02-a057-11f1-879a-e63113e88e45'],
          ['36c5db76-c9c5-11ee-9883-024220a33380', '5aec3d18-a057-11f1-aa77-e63113e88e45'],
          ['9a64fe92-79a5-11ef-b414-024203d38a06', '5af46768-a057-11f1-a4e6-e63113e88e45'],
          ['cc48ca48-7d3d-11ee-afc9-4217ca18cd30', '5ae4c010-a057-11f1-81f3-e63113e88e45'],
          ['cc48d178-7d3d-11ee-a549-4217ca18cd30', '5ae4d3ac-a057-11f1-9d12-e63113e88e45'],
          ['cc48d876-7d3d-11ee-abbd-4217ca18cd30', '5ae4e7b6-a057-11f1-9d60-e63113e88e45'],
          ['36c5cbfe-c9c5-11ee-8fe6-024220a33380', '5aec22ce-a057-11f1-aaf6-e63113e88e45'],
          ['cc48df56-7d3d-11ee-8e9e-4217ca18cd30', '5ae50610-a057-11f1-b03c-e63113e88e45'],
          ['cc496cd2-7d3d-11ee-abb8-4217ca18cd30', '5ae51ca4-a057-11f1-8434-e63113e88e45'],
          ['cc4976a0-7d3d-11ee-9f5c-4217ca18cd30', '5ae530cc-a057-11f1-b8bb-e63113e88e45'],
          ['b880959c-3474-11ef-91e9-024284af7bf4', '5af07612-a057-11f1-9b9c-e63113e88e45'],
          ['cc497f06-7d3d-11ee-ba0b-4217ca18cd30', '5ae54512-a057-11f1-8c1a-e63113e88e45'],
          ['cc4988e8-7d3d-11ee-bdf5-4217ca18cd30', '5ae55a02-a057-11f1-a5d8-e63113e88e45'],
          ['cc4993e2-7d3d-11ee-8e21-4217ca18cd30', '5ae56ccc-a057-11f1-a8d4-e63113e88e45'],
          ['cc499cf2-7d3d-11ee-bd67-4217ca18cd30', '5ae57ff0-a057-11f1-ab8d-e63113e88e45'],
          ['cc49a490-7d3d-11ee-a1f8-4217ca18cd30', '5ae59292-a057-11f1-907c-e63113e88e45'],
          ['cc49ad14-7d3d-11ee-8942-4217ca18cd30', '5ae5a868-a057-11f1-b64a-e63113e88e45'],
          ['cc49b6f6-7d3d-11ee-a27e-4217ca18cd30', '5ae5bbaa-a057-11f1-8ce7-e63113e88e45'],
          ['34b24dca-0e1a-11f0-aa2f-0242cb220b9e', '5af9a94e-a057-11f1-bb4b-e63113e88e45'],
          ['cc49c088-7d3d-11ee-9ea6-4217ca18cd30', '5ae5cfe6-a057-11f1-863c-e63113e88e45'],
          ['3545f312-c99d-11ee-a4be-222560b74e07', '5aebeaac-a057-11f1-a2a5-e63113e88e45'],
          ['4dbd755a-c99d-11ee-946b-024220a33380', '5aec0550-a057-11f1-a5bf-e63113e88e45'],
          ['c9b75fc4-9ace-11ef-8592-ce9ac48e4e9d', '5af54fca-a057-11f1-b140-e63113e88e45'],
          ['530ed33c-6db0-11f1-b84d-ce9ac48e4e9d', '5b14b2e8-a057-11f1-827b-e63113e88e45'],
          ['c9b6d13a-9ace-11ef-a330-ce9ac48e4e9d', '5af53b3e-a057-11f1-ae75-e63113e88e45'],
          ['889ff24a-a360-11ef-9ca3-ce9ac48e4e9d', '5af58f1c-a057-11f1-be07-e63113e88e45'],
          ['ec09f048-a0e9-11ef-8353-ce9ac48e4e9d', '5af563c0-a057-11f1-b68d-e63113e88e45'],
          ['cf591384-6133-11ef-8bdf-ce9ac48e4e9d', '5af16f90-a057-11f1-bab5-e63113e88e45'],
          ['6f0fc6a2-d424-11f0-a4e1-bc24117fdf42', '5b0c337a-a057-11f1-a852-e63113e88e45'],
          ['a3088ecc-2e11-11ef-a2dc-024284af7bf4', '5aefba92-a057-11f1-a41a-e63113e88e45'],
          ['9359f75e-d667-11f0-8151-bc24117fdf42', '5b0cf382-a057-11f1-be7b-e63113e88e45'],
          ['a3087946-2e11-11ef-8cba-024284af7bf4', '5aef9f94-a057-11f1-998f-e63113e88e45'],
          ['994ae9b4-32fa-11ef-adc7-024289afb0db', '5af04016-a057-11f1-a4d7-e63113e88e45'],
          ['0fdaee62-5fc9-11ef-a069-222560b74e07', '5af15a5a-a057-11f1-b0f9-e63113e88e45'],
          ['885f668e-647d-11ef-ad5a-4217ca18cd30', '5af1a032-a057-11f1-ba8b-e63113e88e45'],
          ['885f7a5c-647d-11ef-b066-4217ca18cd30', '5af1b73e-a057-11f1-99ea-e63113e88e45'],
          ['885f8bb4-647d-11ef-8452-4217ca18cd30', '5af1cff8-a057-11f1-9f41-e63113e88e45'],
          ['885f9e06-647d-11ef-a89e-4217ca18cd30', '5af1e45c-a057-11f1-98be-e63113e88e45'],
          ['885faf18-647d-11ef-850a-4217ca18cd30', '5af1ff32-a057-11f1-a1f6-e63113e88e45'],
          ['885fc1b0-647d-11ef-82cf-4217ca18cd30', '5af218c8-a057-11f1-b975-e63113e88e45'],
          ['885fd3c6-647d-11ef-a299-4217ca18cd30', '5af22f5c-a057-11f1-9ed2-e63113e88e45'],
          ['885fe974-647d-11ef-97d3-4217ca18cd30', '5af248de-a057-11f1-b35f-e63113e88e45'],
          ['885fff86-647d-11ef-bb01-4217ca18cd30', '5af26008-a057-11f1-b6ef-e63113e88e45'],
          ['886014a8-647d-11ef-944a-4217ca18cd30', '5af2789a-a057-11f1-9fd8-e63113e88e45'],
          ['8860446e-647d-11ef-ad4f-4217ca18cd30', '5af2a3a6-a057-11f1-837c-e63113e88e45'],
          ['88605bde-647d-11ef-8e65-4217ca18cd30', '5af2b94a-a057-11f1-acf6-e63113e88e45'],
          ['8860704c-647d-11ef-9eba-4217ca18cd30', '5af2cf02-a057-11f1-ba1e-e63113e88e45'],
          ['88602e48-647d-11ef-8ad5-4217ca18cd30', '5af28ea2-a057-11f1-85aa-e63113e88e45'],
          ['886085be-647d-11ef-9a10-4217ca18cd30', '5af2eb18-a057-11f1-9140-e63113e88e45'],
          ['88609c16-647d-11ef-9d28-4217ca18cd30', '5af30170-a057-11f1-ba55-e63113e88e45'],
          ['8860d7d0-647d-11ef-9418-4217ca18cd30', '5af31606-a057-11f1-92d6-e63113e88e45'],
          ['8860fc88-647d-11ef-8521-4217ca18cd30', '5af32a60-a057-11f1-9c29-e63113e88e45'],
          ['88610fa2-647d-11ef-a242-4217ca18cd30', '5af341d0-a057-11f1-91ee-e63113e88e45'],
          ['88612442-647d-11ef-ab33-4217ca18cd30', '5af35594-a057-11f1-b2bb-e63113e88e45'],
          ['aae5f5de-0ca0-11f1-a6e5-ce9ac48e4e9d', '5b10dfec-a057-11f1-aceb-e63113e88e45'],
          ['88613900-647d-11ef-969f-4217ca18cd30', '5af36aac-a057-11f1-b032-e63113e88e45'],
          ['88614ca6-647d-11ef-b795-4217ca18cd30', '5af37e7a-a057-11f1-8d31-e63113e88e45'],
          ['88616074-647d-11ef-942d-4217ca18cd30', '5af391da-a057-11f1-a36d-e63113e88e45'],
          ['886174c4-647d-11ef-ba96-4217ca18cd30', '5af3a53a-a057-11f1-88fe-e63113e88e45'],
          ['1a3a41f2-6927-11ef-bf11-024263e15226', '5af4043a-a057-11f1-a217-e63113e88e45'],
          ['1a3a62ae-6927-11ef-b420-024263e15226', '5af41966-a057-11f1-972f-e63113e88e45'],
          ['1a3a7ba4-6927-11ef-bc8a-024263e15226', '5af42d16-a057-11f1-b958-e63113e88e45'],
          ['b2713c7c-69f5-11ef-86f2-024263e15226', '5af45372-a057-11f1-a33e-e63113e88e45'],
          ['a8ff6ca4-69f5-11ef-ac3c-024263e15226', '5af440a8-a057-11f1-82cf-e63113e88e45'],
          ['b240a2e0-f5ac-11ef-8f7f-1aaef06c4c9e', '5af8ece8-a057-11f1-b0c8-e63113e88e45'],
          ['a7d474e0-afdc-11f0-9473-024248cbd512', '5b01868c-a057-11f1-9193-e63113e88e45'],
          ['e56655bc-a8d0-11ef-b369-ce9ac48e4e9d', '5af6f44c-a057-11f1-b420-e63113e88e45'],
          ['e5667326-a8d0-11ef-92ee-ce9ac48e4e9d', '5af71f6c-a057-11f1-bd93-e63113e88e45'],
          ['e56647ac-a8d0-11ef-8758-ce9ac48e4e9d', '5af6d6b0-a057-11f1-add2-e63113e88e45'],
          ['e56608b4-a8d0-11ef-abed-ce9ac48e4e9d', '5af66720-a057-11f1-ac15-e63113e88e45'],
          ['e5658bb4-a8d0-11ef-96cd-ce9ac48e4e9d', '5af64df8-a057-11f1-bb30-e63113e88e45'],
          ['e566648a-a8d0-11ef-a77f-ce9ac48e4e9d', '5af70b12-a057-11f1-a281-e63113e88e45'],
          ['e5661854-a8d0-11ef-8e74-ce9ac48e4e9d', '5af682f0-a057-11f1-b215-e63113e88e45'],
          ['e5662682-a8d0-11ef-a784-ce9ac48e4e9d', '5af6a4ba-a057-11f1-8b22-e63113e88e45'],
          ['e5663726-a8d0-11ef-8fcf-ce9ac48e4e9d', '5af6be1e-a057-11f1-beda-e63113e88e45'],
          ['7858d588-529b-11f0-a894-bc24117fdf42', '5afcb044-a057-11f1-9941-e63113e88e45'],
          ['7857f62c-529b-11f0-8256-bc24117fdf42', '5afc9974-a057-11f1-b01c-e63113e88e45'],
          ['ab828710-c488-11f0-a622-5a54e0743c62', '5b03f03e-a057-11f1-99a0-e63113e88e45'],
          ['fbb10fe4-c6b9-11f0-9691-bc24117fdf42', '5b06f2d4-a057-11f1-bc1e-e63113e88e45'],
          ['fbb1bd2c-c6b9-11f0-99a9-bc24117fdf42', '5b07094a-a057-11f1-9835-e63113e88e45'],
          ['fbb1d1fe-c6b9-11f0-80a7-bc24117fdf42', '5b072268-a057-11f1-920b-e63113e88e45'],
          ['fbb1e32e-c6b9-11f0-a81c-bc24117fdf42', '5b073bae-a057-11f1-b3d2-e63113e88e45'],
          ['fbb1f31e-c6b9-11f0-ac94-bc24117fdf42', '5b075634-a057-11f1-a16c-e63113e88e45'],
          ['fbb21880-c6b9-11f0-b475-bc24117fdf42', '5b079216-a057-11f1-93eb-e63113e88e45'],
          ['fbb20642-c6b9-11f0-8eb3-bc24117fdf42', '5b077614-a057-11f1-9622-e63113e88e45'],
          ['fbb22c58-c6b9-11f0-9ee3-bc24117fdf42', '5b07a83c-a057-11f1-9ec4-e63113e88e45'],
          ['fbb23d2e-c6b9-11f0-88bb-bc24117fdf42', '5b07c1c8-a057-11f1-a679-e63113e88e45'],
          ['fbb2647a-c6b9-11f0-89ca-bc24117fdf42', '5b07db86-a057-11f1-a35b-e63113e88e45'],
          ['fbb27b0e-c6b9-11f0-a0c7-bc24117fdf42', '5b07f54e-a057-11f1-bffb-e63113e88e45'],
          ['fbb28dec-c6b9-11f0-a98d-bc24117fdf42', '5b080d04-a057-11f1-8bea-e63113e88e45'],
          ['fbb2a598-c6b9-11f0-81c4-bc24117fdf42', '5b0824e2-a057-11f1-86f7-e63113e88e45'],
          ['fbb2bab0-c6b9-11f0-b619-bc24117fdf42', '5b083d88-a057-11f1-abbe-e63113e88e45'],
          ['fbb2d202-c6b9-11f0-ab2c-bc24117fdf42', '5b0855c0-a057-11f1-8d9d-e63113e88e45'],
          ['fbb2ecb0-c6b9-11f0-8178-bc24117fdf42', '5b086b6e-a057-11f1-8daa-e63113e88e45'],
          ['e5a7c102-21f4-11f1-8357-16d0caa4fd61', '5b131e74-a057-11f1-93c5-e63113e88e45'],
          ['fbb30fba-c6b9-11f0-af2b-bc24117fdf42', '5b0899a4-a057-11f1-a93b-e63113e88e45'],
          ['fbb4ed8a-c6b9-11f0-84b5-bc24117fdf42', '5b0ad44e-a057-11f1-90e3-e63113e88e45'],
          ['fbb2fdc2-c6b9-11f0-9faa-bc24117fdf42', '5b0881e4-a057-11f1-8d6d-e63113e88e45'],
          ['fbb32748-c6b9-11f0-8571-bc24117fdf42', '5b08afac-a057-11f1-8793-e63113e88e45'],
          ['fbb3491c-c6b9-11f0-9de3-bc24117fdf42', '5b08c758-a057-11f1-81f6-e63113e88e45'],
          ['fbb36208-c6b9-11f0-87ec-bc24117fdf42', '5b08de46-a057-11f1-ae56-e63113e88e45'],
          ['fbb39c82-c6b9-11f0-81f3-bc24117fdf42', '5b0924c8-a057-11f1-aef9-e63113e88e45'],
          ['fbb3759a-c6b9-11f0-ab60-bc24117fdf42', '5b08f49e-a057-11f1-a7a0-e63113e88e45'],
          ['fbb38b98-c6b9-11f0-ab68-bc24117fdf42', '5b090c7c-a057-11f1-9613-e63113e88e45'],
          ['fbb3af7e-c6b9-11f0-b078-bc24117fdf42', '5b093b5c-a057-11f1-8810-e63113e88e45'],
          ['fbb3c11c-c6b9-11f0-9935-bc24117fdf42', '5b095128-a057-11f1-972f-e63113e88e45'],
          ['fbb3d5a8-c6b9-11f0-bcf1-bc24117fdf42', '5b09667c-a057-11f1-9eba-e63113e88e45'],
          ['fbb3f2ea-c6b9-11f0-9627-bc24117fdf42', '5b098120-a057-11f1-8a1f-e63113e88e45'],
          ['fbb40b5e-c6b9-11f0-a5a0-bc24117fdf42', '5b0996c4-a057-11f1-944c-e63113e88e45'],
          ['fbb41da6-c6b9-11f0-ac6f-bc24117fdf42', '5b09b406-a057-11f1-a08c-e63113e88e45'],
          ['fbb43160-c6b9-11f0-a03d-bc24117fdf42', '5b09ca86-a057-11f1-a2ca-e63113e88e45'],
          ['fbb44510-c6b9-11f0-b718-bc24117fdf42', '5b09e46c-a057-11f1-aad8-e63113e88e45'],
          ['fbb45852-c6b9-11f0-8cd9-bc24117fdf42', '5b0a0474-a057-11f1-811a-e63113e88e45'],
          ['fbb468a6-c6b9-11f0-ae18-bc24117fdf42', '5b0a2440-a057-11f1-bf06-e63113e88e45'],
          ['fbb47ab2-c6b9-11f0-9f8a-bc24117fdf42', '5b0a425e-a057-11f1-9212-e63113e88e45'],
          ['fbb490c4-c6b9-11f0-9710-bc24117fdf42', '5b0a6194-a057-11f1-9a97-e63113e88e45'],
          ['fbb4ac30-c6b9-11f0-b37f-bc24117fdf42', '5b0a7ecc-a057-11f1-a919-e63113e88e45'],
          ['fbb4c4ae-c6b9-11f0-91c8-bc24117fdf42', '5b0a9f7e-a057-11f1-9f46-e63113e88e45'],
          ['fbb4d840-c6b9-11f0-9c4a-bc24117fdf42', '5b0ab95a-a057-11f1-b8d0-e63113e88e45'],
          ['fbb51634-c6b9-11f0-bf26-bc24117fdf42', '5b0af1d6-a057-11f1-ba8e-e63113e88e45'],
          ['fbb52ae8-c6b9-11f0-a174-bc24117fdf42', '5b0b0c84-a057-11f1-b4ac-e63113e88e45'],
          ['fbb544d8-c6b9-11f0-b9cc-bc24117fdf42', '5b0b2a3e-a057-11f1-a5ac-e63113e88e45'],
          ['fbb55cd4-c6b9-11f0-ae6b-bc24117fdf42', '5b0b41e0-a057-11f1-8759-e63113e88e45'],
          ['fbb575d4-c6b9-11f0-8995-bc24117fdf42', '5b0b5784-a057-11f1-8be9-e63113e88e45'],
          ['fbb58d26-c6b9-11f0-bc9a-bc24117fdf42', '5b0b764c-a057-11f1-bc7f-e63113e88e45'],
          ['fbb59fdc-c6b9-11f0-8831-bc24117fdf42', '5b0b90b4-a057-11f1-8087-e63113e88e45'],
          ['fbb5b56c-c6b9-11f0-9b8a-bc24117fdf42', '5b0baa5e-a057-11f1-8707-e63113e88e45'],
          ['fbb5c8fe-c6b9-11f0-8660-bc24117fdf42', '5b0bc548-a057-11f1-b27b-e63113e88e45'],
          ['f4b4deee-c5da-11f0-b4fa-5a54e0743c62', '5b0419b0-a057-11f1-9b14-e63113e88e45'],
          ['f4b5921c-c5da-11f0-8d7f-5a54e0743c62', '5b0443b8-a057-11f1-811f-e63113e88e45'],
          ['f4b59fc8-c5da-11f0-8421-5a54e0743c62', '5b04575e-a057-11f1-abc6-e63113e88e45'],
          ['548ec3f6-df1d-11f0-b486-024214f522cd', '5b0d24ce-a057-11f1-be1e-e63113e88e45'],
          ['f4b5acd4-c5da-11f0-88e2-5a54e0743c62', '5b047040-a057-11f1-a2bf-e63113e88e45'],
          ['f4b5ed8e-c5da-11f0-ae3b-5a54e0743c62', '5b04c89c-a057-11f1-a3bf-e63113e88e45'],
          ['2b014668-c5db-11f0-a210-5a54e0743c62', '5b0517c0-a057-11f1-9baf-e63113e88e45'],
          ['4cb1d8f8-df1d-11f0-9561-024214f522cd', '5b0d0cf0-a057-11f1-bfee-e63113e88e45'],
          ['2b01c2b4-c5db-11f0-8559-5a54e0743c62', '5b052c24-a057-11f1-a552-e63113e88e45'],
          ['33a0edcc-c5e1-11f0-ba66-5a54e0743c62', '5b067a48-a057-11f1-892c-e63113e88e45'],
          ['f4b5db50-c5da-11f0-8c7c-5a54e0743c62', '5b04b3e8-a057-11f1-b54f-e63113e88e45'],
          ['2b01d1f0-c5db-11f0-9ec9-5a54e0743c62', '5b054060-a057-11f1-99ec-e63113e88e45'],
          ['2b01e140-c5db-11f0-a725-5a54e0743c62', '5b05594c-a057-11f1-9e98-e63113e88e45'],
          ['2b01f02c-c5db-11f0-baea-5a54e0743c62', '5b0574c2-a057-11f1-a8ab-e63113e88e45'],
          ['fe24c642-c5da-11f0-a7a4-5a54e0743c62', '5b04ff4c-a057-11f1-bbfe-e63113e88e45'],
          ['fe243f4c-c5da-11f0-8ab2-5a54e0743c62', '5b04e7e6-a057-11f1-b659-e63113e88e45'],
          ['2b01fd9c-c5db-11f0-a3f5-5a54e0743c62', '5b058df4-a057-11f1-b13f-e63113e88e45'],
          ['f4b5bb98-c5da-11f0-a77d-5a54e0743c62', '5b0484fe-a057-11f1-b92c-e63113e88e45'],
          ['64d7211e-c5db-11f0-b1c9-5a54e0743c62', '5b05d2be-a057-11f1-9d42-e63113e88e45'],
          ['f4b58402-c5da-11f0-bedf-5a54e0743c62', '5b042dba-a057-11f1-ba27-e63113e88e45'],
          ['6da0f756-b3df-11f0-98fb-02425e7135e4', '5b0208b4-a057-11f1-b537-e63113e88e45'],
          ['76afc710-c5db-11f0-8e82-5a54e0743c62', '5b05e772-a057-11f1-930f-e63113e88e45'],
          ['99628368-bed5-11f0-9f64-02425e7135e4', '5b03a78c-a057-11f1-aba7-e63113e88e45'],
          ['76b0780e-c5db-11f0-9998-5a54e0743c62', '5b03f03e-a057-11f1-99a0-e63113e88e45'],
          ['2b020b34-c5db-11f0-8d1a-5a54e0743c62', '5b05a924-a057-11f1-b696-e63113e88e45'],
          ['f4b5cb56-c5da-11f0-ae4a-5a54e0743c62', '5b0499a8-a057-11f1-b0a5-e63113e88e45'],
          ['2b021b2e-c5db-11f0-9906-5a54e0743c62', '5b05bf18-a057-11f1-9ab5-e63113e88e45'],
          ['76b086b4-c5db-11f0-872f-5a54e0743c62', '5b05fb9a-a057-11f1-9499-e63113e88e45'],
          ['76b09622-c5db-11f0-b49e-5a54e0743c62', '5b06109e-a057-11f1-af69-e63113e88e45'],
          ['76b0a4f0-c5db-11f0-a063-5a54e0743c62', '5b062872-a057-11f1-ab66-e63113e88e45'],
          ['8be73988-c5db-11f0-96c8-5a54e0743c62', '5b065fb8-a057-11f1-acc0-e63113e88e45'],
          ['76b0ca0c-c5db-11f0-a0ec-5a54e0743c62', '5b0643c0-a057-11f1-af68-e63113e88e45'],
          ['3c855cd2-d4ca-11f0-bab7-024214f522cd', '5b0c484c-a057-11f1-8553-e63113e88e45'],
          ['14e7c916-621b-11f0-9e34-bc2411422144', '5afeacd2-a057-11f1-bb7a-e63113e88e45'],
          ['1b1f07f2-581d-11f0-8366-bc24117fdf42', '5afdb548-a057-11f1-935d-e63113e88e45'],
          ['14e72f60-621b-11f0-9c78-bc2411422144', '5afe6a7e-a057-11f1-9ba2-e63113e88e45'],
          ['14e7b0a2-621b-11f0-b6df-bc2411422144', '5afe7f50-a057-11f1-a6c1-e63113e88e45'],
          ['14e7d3de-621b-11f0-8a69-bc2411422144', '5afec136-a057-11f1-bfe9-e63113e88e45'],
          ['14e7bdc2-621b-11f0-b115-bc2411422144', '5afe954e-a057-11f1-a003-e63113e88e45'],
          ['f590cb96-608d-11f0-aa6f-222560b74e07', '5afe5606-a057-11f1-b76b-e63113e88e45'],
          ['f590b69c-608d-11f0-8806-222560b74e07', '5afe4292-a057-11f1-89d9-e63113e88e45'],
          ['f590a508-608d-11f0-8ee0-222560b74e07', '5afe2d98-a057-11f1-ad72-e63113e88e45'],
          ['f59020ce-608d-11f0-96da-222560b74e07', '5afe17cc-a057-11f1-bd47-e63113e88e45'],
          ['2b111b76-10a2-11f1-8321-024250e21381', '5b11431a-a057-11f1-8ce8-e63113e88e45'],
          ['2b11b252-10a2-11f1-9fe5-024250e21381', '5b117024-a057-11f1-88ec-e63113e88e45'],
          ['2b119f7e-10a2-11f1-bef3-024250e21381', '5b115a94-a057-11f1-a868-e63113e88e45'],
          ['bcc82f5c-ae76-11f0-8dee-02425e7135e4', '5b012818-a057-11f1-ac59-e63113e88e45'],
          ['6cc7e8bc-86a7-11f1-aa3a-8a999dcc40f3', '5b1822de-a057-11f1-8317-e63113e88e45'],
          ['56801552-86a7-11f1-87b8-8a999dcc40f3', '5b17cd20-a057-11f1-b978-e63113e88e45'],
          ['567f67e2-86a7-11f1-8550-8a999dcc40f3', '5b17b6b4-a057-11f1-935c-e63113e88e45'],
          ['56804176-86a7-11f1-860d-8a999dcc40f3', '5b180d3a-a057-11f1-afb9-e63113e88e45'],
          ['43678de8-96f5-11f1-94e5-d64ce83cdeea', '5b190b0e-a057-11f1-a62c-e63113e88e45'],
          ['de23dc46-86a2-11f1-bca6-8a999dcc40f3', '5b17a160-a057-11f1-985c-e63113e88e45'],
          ['5680330c-86a7-11f1-8120-8a999dcc40f3', '5b17f836-a057-11f1-984b-e63113e88e45'],
          ['56802470-86a7-11f1-ae3b-8a999dcc40f3', '5b17e2c4-a057-11f1-9ed6-e63113e88e45'],
          ['212c98a6-89c4-11f1-bbd8-8e70ec523a73', '5b184368-a057-11f1-8be4-e63113e88e45'],
          ['56804f04-86a7-11f1-9ce3-8a999dcc40f3', '5b02c9b6-a057-11f1-b2ad-e63113e88e45'],
          ['01c546b4-810b-11f1-8638-d23673154e14', '5b15d36c-a057-11f1-807e-e63113e88e45'],
          ['2382e9b6-b8be-11f0-99d5-02425e7135e4', '5b02b584-a057-11f1-8970-e63113e88e45'],
          ['e5cb4bea-866c-11f1-9c18-8e70ec523a73', '5b173cfc-a057-11f1-8bde-e63113e88e45'],
          ['01c606d0-810b-11f1-8903-d23673154e14', '5b15e8de-a057-11f1-885b-e63113e88e45'],
          ['821b206c-84fe-11f1-8323-8e70ec523a73', '5b16a0ee-a057-11f1-bfdf-e63113e88e45'],
          ['23822e40-b8be-11f0-89aa-02425e7135e4', '5b02a12a-a057-11f1-8ad2-e63113e88e45'],
          ['680d34c2-8421-11f1-9733-8a999dcc40f3', '5b168a3c-a057-11f1-b7dc-e63113e88e45'],
          ['238300ea-b8be-11f0-b536-02425e7135e4', '5b02c9b6-a057-11f1-b2ad-e63113e88e45'],
          ['0202f6cc-be46-11f0-9a05-024248cbd512', '5b0334e6-a057-11f1-b2ad-e63113e88e45'],
          ['0203bc88-be46-11f0-8975-024248cbd512', '5b038f86-a057-11f1-aac5-e63113e88e45'],
          ['0203a220-be46-11f0-b2e3-024248cbd512', '5b03753c-a057-11f1-84b4-e63113e88e45'],
          ['02038baa-be46-11f0-98b8-024248cbd512', '5b035a0c-a057-11f1-98f6-e63113e88e45'],
          ['5bcd6ee4-18cd-11f1-a723-3a4171a886cd', '5b12f34a-a057-11f1-8b4c-e63113e88e45'],
          ['31f85bf6-10da-11f1-ab54-4217ca18cd30', '5b125a2a-a057-11f1-a53b-e63113e88e45'],
          ['31f848a0-10da-11f1-8fd0-4217ca18cd30', '5b1238ba-a057-11f1-ae52-e63113e88e45'],
          ['17537330-10da-11f1-8235-4217ca18cd30', '5b11e054-a057-11f1-9307-e63113e88e45'],
          ['31f7ba52-10da-11f1-9fdb-4217ca18cd30', '5b11fe4a-a057-11f1-a838-e63113e88e45'],
          ['31f83810-10da-11f1-8fbe-4217ca18cd30', '5b121b1e-a057-11f1-9929-e63113e88e45'],
          ['31f86d30-10da-11f1-9470-4217ca18cd30', '5b1278f2-a057-11f1-932c-e63113e88e45'],
          ['5bcde996-18cd-11f1-b072-3a4171a886cd', '5b130916-a057-11f1-b2dc-e63113e88e45'],
          ['fbfc5446-d4f5-11f0-ba26-024214f522cd', '5b0cc2ea-a057-11f1-80db-e63113e88e45'],
          ['03c2a5e0-d4f6-11f0-af37-024214f522cd', '5b0cdbf4-a057-11f1-8253-e63113e88e45'],
          ['317306a4-7abb-11f1-9995-ce9ac48e4e9d', '5b1520e8-a057-11f1-9a68-e63113e88e45'],
          ['b82d52a8-9f9b-11f1-97b0-4eb6409c39c6', '5b192044-a057-11f1-a637-e63113e88e45'],
          ['cc7ee8e0-eaea-11f0-b528-bc24117fdf42', '5b0d90ee-a057-11f1-bbb4-e63113e88e45'],
          ['7e116c94-0725-11f1-8d4d-4217ca18cd30', '5b0fdf34-a057-11f1-92b4-e63113e88e45'],
          ['05b16d8a-072f-11f1-a1d7-4217ca18cd30', '5b1006e4-a057-11f1-becf-e63113e88e45'],
          ['5b284d0c-0733-11f1-97bd-4217ca18cd30', '5b101b48-a057-11f1-9393-e63113e88e45'],
          ['7e1182a6-0725-11f1-b610-4217ca18cd30', '5b0ff2bc-a057-11f1-98b0-e63113e88e45'],
          ['7e10ebca-0725-11f1-b1a4-4217ca18cd30', '5b0fc530-a057-11f1-a3af-e63113e88e45'],
          ['87560978-08be-11f1-8436-ce9ac48e4e9d', '5b10c868-a057-11f1-bca2-e63113e88e45'],
          ['8755faa0-08be-11f1-8e4c-ce9ac48e4e9d', '5b10b31e-a057-11f1-8d4a-e63113e88e45'],
          ['8755ee34-08be-11f1-9dce-ce9ac48e4e9d', '5b109f6e-a057-11f1-a37b-e63113e88e45'],
          ['bbb2738a-26b6-11f1-bea7-16d0caa4fd61', '5b133508-a057-11f1-97a1-e63113e88e45'],
          ['87557b52-08be-11f1-a39c-ce9ac48e4e9d', '5b108920-a057-11f1-90d1-e63113e88e45'],
          ['f464c3a8-8111-11f1-8e4f-d23673154e14', '5b1520e8-a057-11f1-9a68-e63113e88e45'],
          ['f4642326-8111-11f1-926f-d23673154e14', '5b1658c8-a057-11f1-8c7c-e63113e88e45'],
          ['f464d8f2-8111-11f1-9a78-d23673154e14', '5b167178-a057-11f1-a58a-e63113e88e45'],
          ['0a0918a6-8664-11f1-a7de-8a999dcc40f3', '5b170e1c-a057-11f1-a4c5-e63113e88e45'],
          ['df514616-8667-11f1-9bfb-8a999dcc40f3', '5b1725be-a057-11f1-9ed0-e63113e88e45'],
        ];

        offeringFilters.sportIds = offeringFilters.sportIds.map((sportId) => {
          const mapping = oldToNewSportIdMapping.find((mapping) => mapping[0] === sportId);
          if (mapping) {
            return mapping[1];
          } else {
            return sportId;
          }
        });
      }
    }

    if (queryParams.instructorIds) {
      if (typeof queryParams.instructorIds === 'string') {
        offeringFilters.instructorIds = [parseInt(queryParams.instructorIds, 10)];
      } else if (Array.isArray(queryParams.instructorIds)) {
        offeringFilters.instructorIds = queryParams.instructorIds.map((instructorId) => parseInt(instructorId, 10));
      } else {
        console.warn('Invalid data type for offering filter query param instructorIds', queryParams.instructorIds);
      }
    }

    if (queryParams.locationIds && availableLocations?.length > 0) {
      let locationIds: string[];
      if (typeof queryParams.locationIds === 'string') {
        locationIds = [queryParams.locationIds];
      } else if (Array.isArray(queryParams.locationIds)) {
        locationIds = queryParams.locationIds;
      } else {
        console.warn('Invalid data type for offering filter query param locationIds', queryParams.locationIds);
      }

      offeringFilters.locations = availableLocations?.filter((location) => locationIds.includes(location.id));
    }

    if (queryParams.areaIds && availableLocations?.length > 0) {
      let areaIds: string[];
      if (typeof queryParams.areaIds === 'string') {
        areaIds = [queryParams.areaIds];
      } else if (Array.isArray(queryParams.areaIds)) {
        areaIds = queryParams.areaIds;
      } else {
        console.warn('Invalid data type for offering filter query param areaIds', queryParams.areaIds);
      }

      offeringFilters.areas = this.getLocationAreas(areaIds, availableLocations);
    }

    if (queryParams.fieldIds && availableLocations?.length > 0) {
      let fieldIds: string[];
      if (typeof queryParams.fieldIds === 'string') {
        fieldIds = [queryParams.fieldIds];
      } else if (Array.isArray(queryParams.fieldIds)) {
        fieldIds = queryParams.fieldIds;
      } else {
        console.warn('Invalid data type for offering filter query param fieldIds', queryParams.fieldIds);
      }

      offeringFilters.fields = this.getLocationAreaFields(fieldIds, availableLocations);
    }

    if (queryParams.weekdays) {
      if (typeof queryParams.weekdays === 'string') {
        offeringFilters.weekdays = [queryParams.weekdays];
      } else if (Array.isArray(queryParams.weekdays)) {
        offeringFilters.weekdays = [...queryParams.weekdays];
      } else {
        console.warn('Invalid data type for offering filter query param weekdays', queryParams.weekdays);
      }
    }

    this.setBooleanOfferingFilterForQueryParam(offeringFilters, queryParams, 'free');
    this.setBooleanOfferingFilterForQueryParam(offeringFilters, queryParams, 'includeCanceled');
    this.setBooleanOfferingFilterForQueryParam(offeringFilters, queryParams, 'includePast');
    this.setBooleanOfferingFilterForQueryParam(offeringFilters, queryParams, 'includeWaitListed');

    if (queryParams.executionType !== undefined) {
      if (queryParams.executionType === OfferingExecutionType.presence || queryParams.executionType === OfferingExecutionType.online) {
        offeringFilters.executionType = queryParams.executionType;
      } else {
        console.warn('Invalid data type for offering filter query param executionType', queryParams.executionType);
      }
    }

    if (queryParams.startDateRange !== undefined) {
      try {
        offeringFilters.startDateRange = JSON.parse(queryParams.startDateRange);
      } catch (e) {
        console.warn('Invalid data type for offering filter query param startDateRange', queryParams.startDateRange, e);
      }
    }

    if (queryParams.startTimeRange !== undefined) {
      try {
        offeringFilters.startTimeRange = JSON.parse(queryParams.startTimeRange);
      } catch (e) {
        console.warn('Invalid data type for offering filter query param startTimeRange', queryParams.startTimeRange, e);
      }
    }

    if (queryParams.gender !== undefined) {
      if (queryParams.gender === 'male' || queryParams.gender === 'female') {
        offeringFilters.gender = queryParams.gender;
      } else {
        console.warn('Invalid data type for offering filter query param gender', queryParams.gender);
      }
    }

    if (queryParams.age !== undefined) {
      const age = parseInt(queryParams.age, 10);
      if (isNaN(age) === false) {
        offeringFilters.age = age;
      } else {
        console.warn('Invalid data type for offering filter query param age', queryParams.age);
      }
    }

    if (queryParams.targetGroup !== undefined) {
      if (queryParams.targetGroup === 'all') {
        // no filters required
      } else if (queryParams.targetGroup === 'guest' || queryParams.targetGroup === 'member') {
        offeringFilters.targetGroup = queryParams.targetGroup;
      } else {
        console.warn('Invalid data type for offering filter query param targetGroup', queryParams.targetGroup);
      }
    }

    return offeringFilters;
  }

  private setBooleanOfferingFilterForQueryParam(offeringFilters: OfferingFilters, queryParams: OfferingFiltersQueryParams, key: string) {
    if (queryParams[key] !== undefined) {
      if (queryParams[key] === 'true') {
        offeringFilters[key] = true;
      } else if (queryParams[key] === 'false') {
        offeringFilters[key] = false;
      } else {
        console.warn('Invalid data type for offering filter query param', key, queryParams[key]);
      }
    }
  }

  offeringFiltersToRouterParams(offeringFilters: OfferingFilters): OfferingFiltersRouterParams {
    return {
      ...offeringFilters,
      locations: undefined, // location objects get mapped to locationIds in url query params
      locationIds: offeringFilters.locations?.map((location) => location.id),
      areas: undefined, // area objects get mapped to areaIds in url query params
      areaIds: offeringFilters.areas?.map((area) => area.id),
      fields: undefined, // field objects get mapped to fieldIds in url query params
      fieldIds: offeringFilters.fields?.map((field) => field.id),
      startDateRange: offeringFilters.startDateRange ? JSON.stringify(offeringFilters.startDateRange) : undefined,
      startTimeRange: offeringFilters.startTimeRange ? JSON.stringify(offeringFilters.startTimeRange) : undefined,
    };
  }

  offeringFiltersToApiQueryParams(offeringFilters: OfferingFilters): OfferingFiltersApiQueryParams {
    const offeringFiltersApiQueryParams: OfferingFiltersApiQueryParams = {};

    if (offeringFilters.query) {
      offeringFiltersApiQueryParams['filters[query]'] = [offeringFilters.query];
    }

    if (offeringFilters.state === 'onlyBooked') {
      offeringFiltersApiQueryParams['filters[onlyBooked]'] = 1;
    } else if (offeringFilters.state === 'onlyFavorites') {
      offeringFiltersApiQueryParams['filters[isFavorite]'] = 1;
    }

    if (offeringFilters.teamIds) {
      offeringFiltersApiQueryParams['filters[teamIds][]'] = [...offeringFilters.teamIds];
    }
    if (offeringFilters.categoryIds) {
      offeringFiltersApiQueryParams['filters[categoryIds][]'] = [...offeringFilters.categoryIds];
    }
    if (offeringFilters.sportIds) {
      offeringFiltersApiQueryParams['filters[sportIds][]'] = [...offeringFilters.sportIds];
    }
    if (offeringFilters.departmentIds) {
      offeringFiltersApiQueryParams['filters[departmentIds][]'] = [...offeringFilters.departmentIds];
    }
    if (offeringFilters.instructorIds) {
      offeringFiltersApiQueryParams['filters[instructorIds][]'] = [...offeringFilters.instructorIds];
    }
    if (offeringFilters.weekdays) {
      offeringFiltersApiQueryParams['filters[weekdays][]'] = [...offeringFilters.weekdays];
    }

    if (offeringFilters.executionType === OfferingExecutionType.presence) {
      offeringFiltersApiQueryParams['filters[executionTypes][]'] = [OfferingExecutionType.presence, OfferingExecutionType.hybrid];
    } else if (offeringFilters.executionType === OfferingExecutionType.online) {
      offeringFiltersApiQueryParams['filters[executionTypes][]'] = [OfferingExecutionType.online, OfferingExecutionType.hybrid];
    }

    if (offeringFilters.startDateRange?.start) {
      offeringFiltersApiQueryParams['start'] = offeringFilters.startDateRange.start;
    }
    if (offeringFilters.startDateRange?.end) {
      offeringFiltersApiQueryParams['end'] = offeringFilters.startDateRange.end;
    }

    if (offeringFilters.startTimeRange) {
      offeringFiltersApiQueryParams['filters[startTimeRange][start][hours]'] = offeringFilters.startTimeRange.start.hours;
      offeringFiltersApiQueryParams['filters[startTimeRange][start][minutes]'] = offeringFilters.startTimeRange.start.minutes;
      if (offeringFilters.startTimeRange.end) {
        offeringFiltersApiQueryParams['filters[startTimeRange][end][hours]'] = offeringFilters.startTimeRange.end.hours;
        offeringFiltersApiQueryParams['filters[startTimeRange][end][minutes]'] = offeringFilters.startTimeRange.end.minutes;
      }
    }

    if (offeringFilters.includeCanceled === true) {
      offeringFiltersApiQueryParams['filters[includeCanceled]'] = 1;
    }

    if (offeringFilters.includePast === true) {
      offeringFiltersApiQueryParams['filters[includePast]'] = 1;
    }

    if (offeringFilters.includeWaitListed === true) {
      offeringFiltersApiQueryParams['filters[includeWaitListed]'] = 1;
    } else if (offeringFilters.includeWaitListed === false) {
      offeringFiltersApiQueryParams['filters[includeWaitListed]'] = 0;
    }

    if (offeringFilters.free === true) {
      offeringFiltersApiQueryParams['filters[free]'] = 1;
    } else if (offeringFilters.free === false) {
      offeringFiltersApiQueryParams['filters[free]'] = 0;
    }

    if (offeringFilters.gender === 'male') {
      offeringFiltersApiQueryParams['filters[isBookableForMen]'] = 1;
    } else if (offeringFilters.gender === 'female') {
      offeringFiltersApiQueryParams['filters[isBookableForWomen]'] = 1;
    }

    if (typeof offeringFilters.age === 'number') {
      offeringFiltersApiQueryParams['filters[forAge]'] = offeringFilters.age;
    }

    if (offeringFilters.targetGroup === 'guest') {
      offeringFiltersApiQueryParams['filters[isBookableForGuestMembers]'] = 1;
    } else if (offeringFilters.targetGroup === 'member') {
      offeringFiltersApiQueryParams['filters[isBookableForActiveOrPassiveMembers]'] = 1;
    }

    if (offeringFilters.locations) {
      const locationsWithoutSelectedSubAreasOrFields = offeringFilters.locations.filter((location) => {
        return offeringFilters.areas?.find((area) => area.locationId === location.id) === undefined;
      });
      if (locationsWithoutSelectedSubAreasOrFields.length > 0) {
        offeringFiltersApiQueryParams['filters[locationIds][]'] = locationsWithoutSelectedSubAreasOrFields.map((location) => location.id);
      }
    }

    if (offeringFilters.areas) {
      const areasWithoutSelectedFields = offeringFilters.areas.filter((area) => {
        return offeringFilters.fields?.find((field) => field.areaId === area.id) === undefined;
      });
      if (areasWithoutSelectedFields.length > 0) {
        offeringFiltersApiQueryParams['filters[areaIds][]'] = areasWithoutSelectedFields.map((area) => area.id);
      }
    }

    if (offeringFilters.fields) {
      offeringFiltersApiQueryParams['filters[fieldIds][]'] = offeringFilters.fields.map((field) => field.id);
    }

    return offeringFiltersApiQueryParams;
  }

  getLocationAreas(areaIds: string[], locations: Location[]): LocationArea[] {
    return locations.reduce((areas: LocationArea[], location: Location) => {
      location.areas.forEach((area) => {
        if (areaIds.includes(area.id)) {
          areas.push(area);
        }
      });

      return areas;
    }, []);
  }

  getLocationAreaFields(fieldIds: string[], locations: Location[]): LocationAreaField[] {
    const fields = [];
    for (const location of locations) {
      for (const area of location.areas) {
        for (const field of area.fields) {
          if (fieldIds.includes(field.id)) {
            fields.push(field);
          }
        }
      }
    }

    return fields;
  }
}
